import React, { useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import LabelCanvas from './LabelCanvas';
import Inspector from './Inspector';
import LayersPanel from './LayersPanel';
import { useLabelStore } from '../store/useLabelStore';
import { createJob, API_URL, exportLabel, getAuthHeader, messageDErreur } from '../api/client';
import { resolvePdfPayload } from '../api/pdfImport';
import useJobSocket from '../hooks/useJobSocket';
import logo from '../assets/polyprint-logo.png';
import polyprintP from '../assets/polyprint-p.png';
import FileMenu from './FileMenu';
import HistoryMenu from './HistoryMenu';
import FdsStudioPanel from './FdsStudioPanel';
import VersionBadge from './VersionBadge';
import JaugeConcours from './JaugeConcours';
import FilesColumn from './FilesColumn';
import NewDocumentModal from './NewDocumentModal';
import PreferencesModal from './PreferencesModal';
import PdfPreflightModal from './PdfPreflightModal';
import FloatingNavigator from './FloatingNavigator';
import InspirationStudioModal from './InspirationStudioModal';
import TabBar from './TabBar';
import ToolPalette from './ToolPalette';
import EraserOptionsBar from './EraserOptionsBar';
import SelectionOptionsBar from './SelectionOptionsBar';
import { useAIStudioStore } from '../store/useAIStudioStore';
import { useTabsStore } from '../store/useTabsStore';
import useTabSync from '../hooks/useTabSync';
import HelpTarget from './HelpTarget';
import ShapeLibraryPanel from './ShapeLibraryPanel';
import UserProfileModal from './UserProfileModal';
import ShareModal from './ShareModal';
import ExportDialog from './ExportDialog';
import RegulatoryImportModal from './RegulatoryImportModal';
import RegulatoryTemplateScaleModal from './RegulatoryTemplateScaleModal';
import { useAuthStore } from '../store/useAuthStore';
import { extractRegulatoryTemplate, applyRegulatoryTemplate, isRegulatoryTemplate,
         instantiateRegulatoryTemplate, formatDifferent, mettreALEchelleModele,
         PICTO_MIN_MM, CORPS_MIN_PT } from '../utils/regulatoryTemplate';
import { mentionsIllisibles } from '../utils/regulatoryLisibilite';
import { fitElementsToContent } from '../utils/fitFrame';
import { estDocumentVierge } from '../utils/decoupe';
import { construireProjetPdf } from '../utils/projetImportPdf';
import { lireCsv, decoderCsv, remplirElements, balisesSansColonne, refusDuLot } from '../utils/lotCsv';
import { downloadBlob } from '../utils/download';
import { resolvePictogramShape } from '../utils/regulatoryBlockBuilder';
import { sansDecoupe } from '../utils/decoupeExport';

// ─── Lazy-loaded heavy modules ────────────────────────────────────────────
// Ces 4 composants représentent ~3000 lignes de code combinées, mais sont
// ouverts ponctuellement (clic bouton spécifique, keyboard shortcut) :
//   - AdvancedComfyModal  : modal avancé ComfyUI, niche
//   - AIStudioPanel       : panneau Studio IA, ouvert sur action volontaire
//   - PresentationMode    : mode démo plein écran, usage occasionnel
//   - VapeInvaders        : easter egg ⇒ jamais chargé en usage normal
// → on les retire du bundle initial pour accélérer l'ouverture de l'éditeur.
import ZoneProtegee from './ZoneProtegee';

// Repli d'une zone de droite en panne : même largeur et même fond que
// l'Inspecteur (320 px), pour que la mise en page ne saute pas.
const REPLI_DROITE = "w-[320px] shrink-0 bg-black/40 backdrop-blur-xl border-l border-white/5";
// Repli d'une fenêtre superposée : couvre l'écran comme la fenêtre l'aurait fait.
const REPLI_SUPERPOSE = "fixed inset-0 z-50 bg-black/70 backdrop-blur-sm";

const AdvancedComfyModal = lazy(() => import('./AdvancedComfyModal'));
const AIStudioPanel = lazy(() => import('./ai-studio/AIStudioPanel'));
const GuidePanel = lazy(() => import('./help/GuidePanel'));
const PresentationMode = lazy(() => import('./PresentationMode'));
const VapeInvaders = lazy(() => import('./VapeInvaders'));

// Fallback minimal : un overlay sombre transitoire le temps du chunk fetch
// (typiquement < 500ms). Pas de spinner agressif pour ne pas faire trembler
// l'UI sur les chargements quasi-instantanés du cache navigateur.
const LazyOverlayFallback = () => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center pointer-events-none">
    <div className="flex items-center gap-3 px-5 py-3 bg-black/40 rounded-full border border-white/10 backdrop-blur-sm">
      <div className="w-3 h-3 border-2 border-white/15 border-t-white/60 rounded-full animate-spin" />
      <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">Chargement…</span>
    </div>
  </div>
);

// Helpers purs partagés entre TopActionBar (bouton "Réparer") et EditorView
// (couleur par défaut des shapes ajoutées via ShapeLibraryPanel). Définis au
// top-level pour éviter toute capture incorrecte de scope par hot reload /
// re-render (le bug "elements is not defined" en prod venait de là).

/** Renvoie la luminance relative (0..1) d'une couleur hex, ou null si non parsable. */
function luminanceOfHex(bg) {
    if (!bg || typeof bg !== 'string' || !bg.startsWith('#')) return null;
    let hex = bg.slice(1);
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) return null;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/** Couleur (#000 ou #FFF) qui contraste avec le fond passé en paramètre. */
function pickContrastColor(bg) {
    const lum = luminanceOfHex(bg);
    if (lum === null) return '#000000';
    return lum > 0.55 ? '#000000' : '#FFFFFF';
}

const NumberInput = ({ value, onChange, className = "", min = null, max = null, step = 1, disabled = false }) => {
    return (
        <div className={`flex bg-black/20 border border-white/10 rounded overflow-hidden ${className}`}>
            <button
                type="button"
                disabled={disabled}
                className="px-1.5 hover:bg-white hover:text-black text-white/40 transition-colors font-bold select-none text-[10px] disabled:opacity-50"
                onClick={() => { const v = Number((value - step).toFixed(2)); if (min === null || v >= min) onChange(v); }}
            >
                -
            </button>
            <input
                type="number"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-8 md:w-10 bg-transparent text-center outline-none text-[10px] font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
            />
            <button
                type="button"
                disabled={disabled}
                className="px-1.5 hover:bg-white hover:text-black text-white/40 transition-colors font-bold select-none text-[10px] disabled:opacity-50"
                onClick={() => { const v = Number((value + step).toFixed(2)); if (max === null || v <= max) onChange(v); }}
            >
                +
            </button>
        </div>
    );
};

// onOpenFds a été retirée des props : le bouton FDS du header a disparu le
// 2026-07-14 (demande client, cf. le commentaire dans le bloc central) et la
// prop est restée derrière lui, alimentée mais jamais lue. FDS Studio s'ouvre
// par l'onglet du dock droit (onSelectFds), plus bas dans ce fichier.
const TopActionBar = ({ onOpenAI, onOpenInspiration, onOpenPreflight, onOpenNew, onOpenProfile, onOpenShare, onOpenExport, onAddText, onAddBarcode, onAddImage, onAddShape, onOpenShapeLibrary, aiStatus, aiProgress, isGeneratingLocally }) => {
    const { user } = useAuthStore();
    const { elements, canvas, aiImage, setCanvas, loadProject, getProjectData, patchElements, addElement } = useLabelStore();
    const toolPaletteOpen = useLabelStore((s) => s.toolPalette.open);
    // Le Guide est-il la vue affichée dans le dock ? Sert à allumer le « ? » :
    // un interrupteur doit montrer son état, sinon on clique deux fois pour voir.
    const helpOuvert = useAIStudioStore((s) => s.isOpen && s.view === 'help');
    const [exporting, setExporting] = React.useState(false);

    // Dégressivité de la barre selon SA propre largeur (cf. globals.css pour
    // pourquoi ce n'est pas une container query). Les seuils sont mesurés sur
    // le contenu réel : ~420 px à gauche + 310 au centre + 220 à droite.
    const toolbarRef = React.useRef(null);
    React.useEffect(() => {
        const node = toolbarRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(([entry]) => {
            const w = entry.contentRect.width;
            node.dataset.dense = w < 720 ? 'max' : w < 950 ? 'menus' : 'none';
        });
        ro.observe(node);
        return () => ro.disconnect();
    }, []);
    const fileInputRef = React.useRef(null);
    const csvInputRef = React.useRef(null);
    const pdfInputRef = React.useRef(null);
    const lfgrInputRef = React.useRef(null);
    // Toast non-bloquant pour le retour d'application d'un modèle réglementaire.
    const [regToast, setRegToast] = React.useState(null); // { type: 'ok'|'warn'|'err', text }
    const regToastTimer = React.useRef(null);
    const showRegToast = React.useCallback((type, text) => {
        setRegToast({ type, text });
        if (regToastTimer.current) clearTimeout(regToastTimer.current);
        regToastTimer.current = setTimeout(() => setRegToast(null), 7000);
    }, []);

    // Détecte les shapes SVG dont la couleur visuelle est trop proche du fond
    // du canvas (delta luminance < 0.20 = quasi-invisibles). Sert à afficher
    // un bouton "Réparer N picto" dans la toolbar quand on charge un ancien
    // projet créé avant le fix de couleur par défaut.
    const invisibleShapeIds = React.useMemo(() => {
        const bgLum = luminanceOfHex(canvas.color_bg);
        if (bgLum === null) return [];   // fond gradient/transparent → on ne décide pas
        return elements
            .filter(el =>
                el.type === 'shape' &&
                el.shapeType === 'svg' &&
                el.svgPath &&            // skip multi-color (svgContent fixe non modifiable)
                !el.locked
            )
            .filter(el => {
                const colorLum = luminanceOfHex(el.color || '#FFFFFF');
                if (colorLum === null) return false;
                return Math.abs(colorLum - bgLum) < 0.20;
            })
            .map(el => el.id);
    }, [elements, canvas.color_bg]);

    const repairInvisibleShapes = () => {
        const contrast = pickContrastColor(canvas.color_bg);
        const ids = invisibleShapeIds;
        if (!ids.length) return;
        const updateElement = useLabelStore.getState().updateElement;
        ids.forEach(id => updateElement(id, { color: contrast }));
    };

    // ─── Auto-save indicator ───────────────────────────────────────────
    // Le store est persisté en IndexedDB en continu (Zustand persist middleware),
    // donc chaque modif est sauvegardée immédiatement. On expose juste un retour
    // visuel : pulse "Sauvegarde…" pendant ~600ms après chaque modification puis
    // bascule sur "Sauvegardé" stable. Évite l'angoisse "je viens de modifier,
    // est-ce que c'est bien gardé si je ferme la fenêtre ?".
    const [saveState, setSaveState] = React.useState('saved'); // 'saving' | 'saved'
    const saveTimerRef = React.useRef(null);
    const firstRunRef = React.useRef(true);
    React.useEffect(() => {
        if (firstRunRef.current) {
            // Skip le mount initial — on ne veut pas afficher "Sauvegarde…" au load
            // alors qu'on n'a rien modifié.
            firstRunRef.current = false;
            return;
        }
        setSaveState('saving');
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => setSaveState('saved'), 600);
        return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    }, [elements, canvas]);

    const handleExport = async (format, vectorize = false) => {
        setExporting(true);
        try {
            // ─── PNG EXPORT — capture client-side (raster), tel qu'affiché ───
            if (format === 'png') {
                const { default: html2canvas } = await import('html2canvas');

                // Find the canvas container element (the actual label area)
                const canvasContainer = document.querySelector('[data-export-canvas]');
                if (!canvasContainer) throw new Error("le plan de travail est introuvable. Rouvrez le document, puis relancez l'export.");

                // ─── Pre-export: fix html2canvas incompatibilities ───

                // 1) Hide selection UI, guides, grid
                const selectionElements = canvasContainer.querySelectorAll('.group svg.absolute, [class*="z-50"]');
                selectionElements.forEach(el => el.style.display = 'none');

                // 2) Workaround: html2canvas doesn't support background-clip:text
                //    → Replace gradient text divs with a solid color fallback temporarily
                const gradientTextFixes = [];
                canvasContainer.querySelectorAll('div').forEach(div => {
                    const style = window.getComputedStyle(div);
                    if (style.webkitBackgroundClip === 'text' || style.backgroundClip === 'text') {
                        const bgImage = style.backgroundImage;
                        if (bgImage && bgImage !== 'none') {
                            gradientTextFixes.push({
                                el: div,
                                origColor: div.style.color,
                                origBgImage: div.style.backgroundImage,
                                origBgClip: div.style.webkitBackgroundClip,
                                origBgClip2: div.style.backgroundClip,
                            });
                            const colorMatch = bgImage.match(/rgb\([^)]+\)|#[0-9a-fA-F]+/);
                            const fallbackColor = colorMatch ? colorMatch[0] : '#FFFFFF';
                            div.style.color = fallbackColor;
                            div.style.backgroundImage = 'none';
                            div.style.webkitBackgroundClip = 'border-box';
                            div.style.backgroundClip = 'border-box';
                        }
                    }
                });

                // Capture at high DPI (3x for 300 DPI print quality)
                const capturedCanvas = await html2canvas(canvasContainer, {
                    scale: 3,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: null,
                    logging: false,
                    ignoreElements: (el) => {
                        return el.classList?.contains('ruler-overlay') ||
                               el.getAttribute?.('data-no-export') === 'true';
                    }
                });

                // ─── Post-export: restore everything ───
                selectionElements.forEach(el => el.style.display = '');
                gradientTextFixes.forEach(({ el, origColor, origBgImage, origBgClip, origBgClip2 }) => {
                    el.style.color = origColor;
                    el.style.backgroundImage = origBgImage;
                    el.style.webkitBackgroundClip = origBgClip;
                    el.style.backgroundClip = origBgClip2;
                });

                capturedCanvas.toBlob((blob) => {
                    downloadBlob(blob, `labelforge_export_${new Date().getTime()}.png`);
                }, 'image/png');
            }
            // ─── SVG + PDF EXPORT — moteur serveur vectoriel ───
            //     SVG : build_svg | PDF : build_html + WeasyPrint (texte vectoriel,
            //     polices embarquées, géométrie mm exacte). Plus de html2canvas/jsPDF.
            else if (format === 'svg' || format === 'pdf') {
                const exportElements = [];
                if (aiImage && aiImage.url) {
                    exportElements.push({
                        id: 'ai_background', type: 'image', url: aiImage.url,
                        x: aiImage.x || 0, y: aiImage.y || 0,
                        width: aiImage.width || canvas.width_mm, height: aiImage.height || canvas.height_mm,
                        opacity: 90, rotation: 0,
                    });
                }
                exportElements.push(...elements.filter(e => e.type !== 'guide'));
                const payload = {
                    document: { width: canvas.width_mm, height: canvas.height_mm, backgroundColor: canvas.color_bg },
                    elements: exportElements, format, vectorize_text: vectorize
                };
                const blob = await exportLabel(payload, format);
                downloadBlob(blob, `labelforge_export_${new Date().getTime()}.${format}`);
            }
        } catch (err) {
            alert("Erreur lors de l'exportation : " + err.message);
        } finally {
            setExporting(false);
        }
    };

    const handleDoubleExport = async () => {
        await handleExport('pdf');
    };

    const _buildProjectFilename = () => {
        // Nom de fichier = nom de l'ONGLET (= nom du PDF importé, ou "JJ-MM-AA-LxH"
        // pour un document vierge). Cohérent avec ce que l'utilisateur voit.
        const tab = useTabsStore.getState().getActiveTab?.();
        const raw = (tab?.name || '').trim();
        const safe = raw.replace(/[\/\\:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim();
        if (safe) return safe;
        // Repli : dimensions du document
        const data = getProjectData();
        const w = Math.round(data.canvas?.width_mm || 130);
        const h = Math.round(data.canvas?.height_mm || 80);
        return `projet_${w}x${h}`;
    };

    const handleSaveProject = async () => {
        const projectData = JSON.stringify(getProjectData(), null, 2);
        const baseName = _buildProjectFilename();

        // Nom retenu pour l'onglet. Il vaut le nom SUGGÉRÉ tant que l'utilisateur
        // n'en a pas choisi un autre dans la boîte d'enregistrement.
        let nomRetenu = baseName;

        try {
            // Seule l'écriture par le sélecteur de fichier PROUVE que le projet est
            // sur le disque : elle échoue en exception si le fichier n'est pas écrit.
            let ecritureConfirmee = false;
            if (window.showSaveFilePicker) {
                const handle = await window.showSaveFilePicker({
                    suggestedName: `${baseName}.lfg`,
                    types: [{
                        description: 'Projet LabelForge',
                        accept: { 'application/json': ['.lfg'] },
                    }],
                });
                // LE NOM QUI COMPTE EST CELUI DU FICHIER ÉCRIT, pas celui qu'on
                // avait suggéré. Renommer l'onglet en `baseName` revenait à ignorer
                // le nom saisi dans la boîte d'enregistrement : on venait de créer
                // « Puff Nation v3.lfg » et l'onglet s'appelait toujours comme
                // avant, si bien que l'enregistrement SUIVANT proposait de nouveau
                // l'ancien nom. `handle.name` porte le nom réel, extension comprise.
                const nomFichier = (handle.name || '').replace(/\.(lfg|json)$/i, '').trim();
                if (nomFichier) nomRetenu = nomFichier;
                const writable = await handle.createWritable();
                await writable.write(projectData);
                await writable.close();
                ecritureConfirmee = true;
            } else {
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(projectData);
                const a = document.createElement('a');
                a.href = dataStr;
                a.download = `${baseName}.lfg`;
                a.click();
                showRegToast('warn', "Téléchargement du projet lancé. L'astérisque reste tant que l'enregistrement n'est pas confirmé : vérifiez le fichier dans vos téléchargements.");
            }
            const tabsState = useTabsStore.getState();
            tabsState.renameTab(tabsState.activeTabId, nomRetenu);
            // L'astérisque tombe dans les DEUX voies, y compris le repli par
            // téléchargement où `a.click()` ne prouve rien.
            //
            // Ce n'est pas un oubli, c'est le moins mauvais des deux défauts. Ne le
            // retirer que sur écriture prouvée revenait à le laisser DÉFINITIF sur
            // Firefox et Safari, qui n'ont pas `showSaveFilePicker` : tous les
            // enregistrements y passent par le repli, l'astérisque ne serait jamais
            // tombé, et la confirmation de fermeture d'onglet se serait déclenchée à
            // chaque fois — un avertissement qui crie toujours cesse d'être entendu.
            // On préfère donc le dire franchement, par le message ci-dessus, plutôt
            // que d'entraîner l'utilisateur à ignorer ses alertes.
            tabsState.markClean();
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error("Erreur de sauvegarde du projet:", err);
            }
        }
    };

    const handleLoadProject = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const projectData = JSON.parse(event.target.result);
                if (projectData.canvas && projectData.elements) {
                    // Open in a new tab
                    const fileName = file.name.replace(/\.(lfg|json)$/i, '') || 'Sans titre';
                    useTabsStore.getState().addTab(fileName, projectData);
                    // loadProject will be triggered by useTabSync when the new tab becomes active
                } else {
                    alert("Ce fichier n'est pas un projet LabelForge : il ne contient ni document ni calques. Ouvrez un fichier enregistré depuis LabelForge (.lfg).");
                }
            } catch (err) {
                alert("Ce fichier projet est illisible (JSON invalide). Réenregistrez-le depuis LabelForge, ou ouvrez une autre version.\n\nDétail : " + err.message);
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const handleImportCSV = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const { entetes, lignes } = lireCsv(decoderCsv(event.target.result));
                // AVANT : un CSV vide, réduit à sa seule ligne d'en-tête, ou tout en
                // blancs, repartait par un `return` muet — ni toast ni alerte. Rien ne
                // se passait à l'écran et le bouton « Importer une base CSV » passait
                // pour cassé, alors que c'est le fichier qui n'avait pas de données.
                if (!entetes) {
                    showRegToast('err', "Ce fichier CSV est vide : il lui faut une ligne d'en-tête (les noms de colonnes) puis une ligne par étiquette à générer.");
                    return;
                }
                if (lignes.length === 0) {
                    showRegToast('err', "Ce fichier CSV ne contient que sa ligne d'en-tête : ajoutez une ligne de données par étiquette à générer, puis relancez l'import.");
                    return;
                }
                // L'ancien moteur du lot ne sait pas poser un ton direct : la découpe
                // en est retirée, pour qu'il ne l'imprime pas dans l'étiquette.
                const modele = sansDecoupe(elements.filter(e => e.type !== 'guide'));
                const etiquettes = lignes.map((ligne) => remplirElements(modele, ligne));
                // Tout le lot est vérifié AVANT le premier téléchargement : une
                // valeur qui ne se code pas (EAN-13 de 11 chiffres…) arrêtait sinon
                // le lot en route, quelques PDF déjà téléchargés et les autres non.
                const refus = await refusDuLot(etiquettes);
                if (refus.length > 0) {
                    const combien = refus.length > 1 ? `${refus.length} codes-barres du lot ne se codent pas` : 'un code-barres du lot ne se code pas';
                    showRegToast('err', `Aucun PDF généré : ${combien}. ${refus.slice(0, 3).join(' ; ')}${refus.length > 3 ? ' ; …' : ''}`);
                    return;
                }
                const orphelines = balisesSansColonne(modele, entetes);
                const avertissement = orphelines.length > 0
                    ? `\n\nAttention, aucune colonne du fichier ne remplit ${orphelines.map(c => `{{${c}}}`).join(', ')} : ce texte sortira tel quel dans chaque PDF.`
                    : '';
                if (!window.confirm(`Générer ${lignes.length} PDF, un par ligne du fichier ?${avertissement}`)) return;
                setExporting(true);
                for (let i = 0; i < lignes.length; i++) {
                    const payload = { document: { width: canvas.width_mm, height: canvas.height_mm, backgroundColor: canvas.color_bg }, elements: etiquettes[i], format: 'pdf' };
                    const blob = await exportLabel(payload, 'pdf');
                    downloadBlob(blob, `labelforge_${lignes[i][entetes[0]] || `export_${i + 1}`}.pdf`);
                    await new Promise(r => setTimeout(r, 500));
                }
            } catch (err) { alert("L'import CSV a échoué : " + err.message); } finally { setExporting(false); }
        };
        // Les octets, pas du texte : l'encodage se décide dans decoderCsv (UTF-8 ou Windows-1252).
        reader.readAsArrayBuffer(file);
        e.target.value = '';
    };

    // ─── Import PDF — multi-fichiers + modèle réglementaire optionnel ───
    const [pdfPendingFiles, setPdfPendingFiles] = React.useState([]); // File[]
    const [pdfTemplate, setPdfTemplate] = React.useState(null);       // { name, template } | null
    // { total, current, mode, phase: 'upload' | 'server', upload?: 0..1 } | null
    // `phase` est explicite et non déduit du ratio : rien ne garantit qu'un dernier
    // événement de progression arrive à exactement 1, et l'écran resterait alors
    // bloqué sur « Envoi du fichier · 99 % » pendant tout le traitement serveur.
    const [importStatus, setImportStatus] = React.useState(null);
    const [importElapsed, setImportElapsed] = React.useState(0);      // secondes écoulées
    const pdfTemplateInputRef = React.useRef(null);
    // Prises d'annulation de l'import en cours : { xhr, controller, cancelled }.
    // Sans elles, un envoi de 100 Mo sur une ligne d'atelier verrouille la modale
    // pendant plusieurs minutes, sans croix ni Échap, et continue de consommer la
    // bande montante même après un rechargement de page.
    const importAbortRef = React.useRef(null);

    // Libellés des modes d'import, tels qu'affichés sur les cartes de la modale.
    const PDF_MODE_LABELS = { vector: 'Fidèle + calques', extract: 'Fond fidèle', template: 'Aplati' };
    // Ce que le serveur fait vraiment, pour que l'attente ait un sens.
    const PDF_MODE_HINTS = {
        vector: "Le PDF est rendu en fond fidèle, puis ses textes et tracés de découpe sont analysés un par un pour devenir des calques éditables. C'est le mode le plus long : comptez plusieurs dizaines de secondes sur un fichier chargé.",
        extract: 'Le PDF est rendu côté serveur en image fidèle, puis les textes détectés sont extraits.',
        template: 'Le PDF est rendu côté serveur en une image unique, verrouillée en fond de document.',
    };

    // Chrono de l'import : la preuve visible que le traitement avance encore.
    // Le serveur ne renvoie rien avant d'avoir fini (requête unique), donc sans
    // ce compteur l'écran paraît figé sur les gros fichiers.
    React.useEffect(() => {
        if (!importStatus) { setImportElapsed(0); return; }
        setImportElapsed(0);
        const startedAt = Date.now();
        const id = setInterval(() => setImportElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
        return () => clearInterval(id);
    }, [importStatus?.mode, importStatus?.current, importStatus?.total]);

    // Interrompt l'import en cours : l'envoi S3 (XHR) comme la requête serveur.
    const cancelPdfImport = () => {
        const h = importAbortRef.current;
        if (!h) return;
        h.cancelled = true;
        try { h.xhr?.abort(); } catch (_) { /* déjà terminé */ }
        try { h.controller?.abort(); } catch (_) { /* déjà terminé */ }
    };

    // Seuil de bascule entre les deux voies d'envoi (côté serveur : MAX_PDF_UPLOAD_SIZE).
    // Cloud Run coupe toute requête au-delà de 32 Mio ; au-dessus de ce seuil, le
    // fichier est donc déposé directement sur S3 et le serveur ne reçoit que sa clé.
    const MAX_PDF_MB = 30;
    // Plafond absolu (côté serveur : MAX_PDF_S3_SIZE).
    const MAX_PDF_S3_MB = 100;

    const handleImportPDF = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;

        const tooBig = files.filter(f => f.size > MAX_PDF_S3_MB * 1024 * 1024);
        if (tooBig.length) {
            const mb = (f) => (f.size / 1024 / 1024).toFixed(1).replace('.', ',');
            showRegToast('err', tooBig.length === 1
                ? `« ${tooBig[0].name} » pèse ${mb(tooBig[0])} Mo — la limite est de ${MAX_PDF_S3_MB} Mo par fichier. Réexportez-le avec une compression d'images, ou découpez-le.`
                : `${tooBig.length} fichiers dépassent ${MAX_PDF_S3_MB} Mo (${tooBig.map(f => `${f.name} : ${mb(f)} Mo`).join(' · ')}). Ils ne peuvent pas être importés en l'état.`);
            const ok = files.filter(f => f.size <= MAX_PDF_S3_MB * 1024 * 1024);
            if (!ok.length) return;   // rien d'importable : on n'ouvre pas la modale
            setPdfTemplate(null); setImportStatus(null); setPdfPendingFiles(ok);
            return;
        }

        setPdfTemplate(null);
        setImportStatus(null);
        setPdfPendingFiles(files);
    };

    // Choix d'un modèle .lfgr à superposer sur les PDF importés.
    const handlePickPdfTemplate = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const tpl = JSON.parse(ev.target.result);
                if (!isRegulatoryTemplate(tpl)) {
                    showRegToast('err', "Ce fichier n'est pas un modèle réglementaire (.lfgr) valide.");
                    return;
                }
                setPdfTemplate({ name: file.name.replace(/\.(lfgr|json)$/i, ''), template: tpl });
            } catch (err) {
                showRegToast('err', 'Fichier .lfgr illisible (JSON invalide).');
            }
        };
        reader.readAsText(file);
    };

    // Dépose le fichier sur S3 via une URL présignée, en rapportant l'avancement
    // réel de l'envoi. Sert aux PDF trop gros pour une requête Cloud Run (32 Mio).
    const uploadPdfToS3 = (file, onProgress) => new Promise((resolve, reject) => {
        const targetApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8090';
        const { authFetch } = useAuthStore.getState();
        const form = new FormData();
        form.append('filename', file.name);
        form.append('size', String(file.size));
        const abortErr = () => Object.assign(new Error('Import annulé.'), { code: 'ABORTED' });
        if (importAbortRef.current?.cancelled) { reject(abortErr()); return; }
        authFetch(`${targetApiUrl}/api/pdf/upload-url`, {
            method: 'POST', body: form, signal: importAbortRef.current?.controller?.signal,
        })
            .then(async (res) => {
                if (!res.ok) {
                    let detail = `Erreur ${res.status}`;
                    try { const j = await res.json(); if (j.detail) detail = messageDErreur(j.detail, detail); } catch (_) { /* not json */ }
                    throw new Error(detail);
                }
                return res.json();
            })
            .then(({ upload_url, s3_key }) => {
                if (importAbortRef.current?.cancelled) { reject(abortErr()); return; }
                // XHR (et non fetch) : lui seul expose la progression de l'envoi.
                const xhr = new XMLHttpRequest();
                if (importAbortRef.current) importAbortRef.current.xhr = xhr;
                xhr.open('PUT', upload_url, true);
                xhr.setRequestHeader('Content-Type', 'application/pdf');
                // Progression rapportée au pourcentage ENTIER près. Chrome émet
                // plusieurs dizaines d'événements par seconde ; chacun provoquait un
                // rendu complet de cette barre d'outils (~1000 lignes de JSX, abonnée
                // au store entier), soit du CPU volé au thread principal exactement
                // pendant l'envoi — pour un affichage qui, lui, est arrondi à l'entier.
                let lastPct = -1;
                xhr.upload.onprogress = (ev) => {
                    if (!ev.lengthComputable || !onProgress) return;
                    const pct = Math.round((ev.loaded / ev.total) * 100);
                    if (pct === lastPct) return;
                    lastPct = pct;
                    onProgress(pct / 100);
                };
                xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
                    ? resolve(s3_key)
                    : reject(new Error(`L'envoi du fichier a échoué (erreur ${xhr.status}). Relancez l'import dans un instant.`));
                // Les messages parlent de ce que l'utilisateur voit — un envoi qui
                // n'aboutit pas — et non du stockage ni de sa politique CORS, qui ne
                // lui apprennent rien et qu'il ne peut pas corriger.
                xhr.onerror = () => reject(new Error("L'envoi du fichier a été interrompu. Vérifiez votre connexion, puis relancez l'import."));
                xhr.ontimeout = () => reject(new Error("L'envoi du fichier a expiré : la connexion est trop lente pour un fichier de cette taille. Relancez l'import, ou allégez le PDF."));
                // Sans ce gestionnaire, un abort() ne règle JAMAIS la promesse : ni
                // résolution, ni rejet — donc pas de finally, et une modale figée à vie.
                xhr.onabort = () => reject(abortErr());
                xhr.timeout = 30 * 60 * 1000;
                xhr.send(file);
            })
            .catch(reject);
    });

    // Importe UN PDF via l'API ; renvoie le JSON ou lève une erreur.
    // Deux voies : requête directe pour les fichiers légers, dépôt S3 au-delà de
    // la limite de Cloud Run (le serveur ne reçoit alors que la clé du fichier).
    const importOnePdf = async (file, mode, onPhase) => {
        const targetApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8090';
        const { authFetch } = useAuthStore.getState();
        const signal = importAbortRef.current?.controller?.signal;
        const formData = new FormData();
        formData.append('mode', mode);

        if (file.size > MAX_PDF_MB * 1024 * 1024) {
            onPhase?.({ phase: 'upload', upload: 0 });
            const s3Key = await uploadPdfToS3(file, (ratio) => onPhase?.({ phase: 'upload', upload: ratio }));
            formData.append('s3_key', s3Key);
            formData.append('filename', file.name);
        } else {
            formData.append('file', file);
        }
        // L'envoi est fini : on bascule explicitement de phase, sans attendre un
        // hypothétique dernier événement de progression à 100 %.
        onPhase?.({ phase: 'server' });

        const res = await authFetch(`${targetApiUrl}/api/pdf/import`, {
            method: 'POST', body: formData, signal,
        });
        if (!res.ok) {
            let detail = `Erreur ${res.status}`;
            try { const j = await res.json(); if (j.detail) detail = messageDErreur(j.detail, detail); } catch (_) { /* not json */ }
            throw new Error(detail);
        }
        // Réponse trop lourde pour la limite de sortie de Cloud Run : le serveur l'a
        // déposée sur S3 et n'a renvoyé qu'une URL. Le rapatriement est obligatoire
        // pour les TROIS modes — « Aplati » range son image à la racine, les deux
        // autres la rangent dans elements[].
        return await resolvePdfPayload(await res.json(), { signal });
    };

    // Construit le projectData d'un onglet (fond PDF + squelette du modèle) :
    // cf. utils/projetImportPdf, qui pose aussi le format fini et le fond perdu.
    const buildPdfProjectData = construireProjetPdf;

    const executePdfImport = async (mode) => {
        const files = pdfPendingFiles;
        if (!files.length) return;
        // Squelette du modèle, instancié une seule fois (réutilisé sur chaque PDF).
        const skeleton = pdfTemplate ? instantiateRegulatoryTemplate(pdfTemplate.template, resolvePictogramShape) : [];

        // Phase de départ : un fichier lourd commence par un envoi vers le stockage,
        // un fichier léger part directement au serveur.
        const firstPhase = (f) => (f.size > MAX_PDF_MB * 1024 * 1024 ? 'upload' : 'server');
        importAbortRef.current = { cancelled: false, controller: new AbortController(), xhr: null };

        // ── 1 seul fichier : NOUVEL onglet nommé d'après le PDF ──
        if (files.length === 1) {
            // La modale RESTE ouverte, en mode progression : l'import peut durer
            // des dizaines de secondes et un écran sans retour laisse croire à un bug.
            setImportStatus({ total: 1, current: 1, mode, phase: firstPhase(files[0]) });
            try {
                const data = await importOnePdf(files[0], mode,
                    (p) => setImportStatus({ total: 1, current: 1, mode, ...p }));
                // Onglet construit depuis le PDF (nom = fichier). On l'ouvre dans un
                // NOUVEL onglet ; si l'onglet courant est vierge (rien que sa découpe
                // générée, non modifié), on le retire pour ne pas laisser traîner un
                // onglet vide.
                const pd = buildPdfProjectData(files[0], data, skeleton, pdfTemplate?.template?.canvas);
                const ts = useTabsStore.getState();
                const prev = ts.getActiveTab();
                const prevPristine = !!prev && !prev.isDirty && estDocumentVierge(useLabelStore.getState().elements);
                ts.addTabsBatch([pd], { activateFirst: true });
                if (prevPristine && prev) {
                    useTabsStore.setState((s) => {
                        const remaining = s.tabs.filter(t => t.id !== prev.id);
                        return remaining.length ? { tabs: remaining } : {};
                    });
                }
                const empty = (data.mode !== 'template') && (!data.elements || data.elements.length === 0);
                if (empty) showRegToast('warn', "Aucun élément extrait — essayez un autre mode d'import.");
                else showRegToast('ok', `« ${pd.name} » importé${skeleton.length ? ' · modèle appliqué' : ''}.`);
            } catch (err) {
                if (err.code === 'ABORTED' || err.name === 'AbortError') showRegToast('warn', 'Import annulé.');
                else if (err.code === 'SESSION_EXPIRED') showRegToast('err', 'Votre session a expiré. Reconnectez-vous, puis relancez l\'import.');
                else if (err.code === 'NETWORK_AUTH_FAILED') showRegToast('err', 'Connexion réseau interrompue. Vérifiez votre connexion, puis relancez l\'import.');
                else showRegToast('err', 'Erreur PDF : ' + err.message);
            } finally {
                importAbortRef.current = null;
                setImportStatus(null); setPdfPendingFiles([]); setPdfTemplate(null);
            }
            return;
        }

        // ── Plusieurs fichiers : un ONGLET par PDF (import séquentiel + progression) ──
        const tabsData = [];
        const errors = [];
        let emptyExtract = 0;
        let cancelled = false;
        let sessionExpired = false;
        setImportStatus({ total: files.length, current: 0, mode, phase: firstPhase(files[0]) });
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setImportStatus({ total: files.length, current: i + 1, mode, phase: firstPhase(file) });
            try {
                const data = await importOnePdf(file, mode,
                    (p) => setImportStatus({ total: files.length, current: i + 1, mode, ...p }));
                // Le décompte ne testait que le mode « extract » : un import multiple
                // en « Fidèle + calques » (mode 'vector') qui ne rapportait aucun
                // calque n'était compté nulle part, et le bilan final ne disait donc
                // RIEN — l'utilisateur repartait avec des onglets vides sans savoir
                // pourquoi. Même critère que l'import d'un seul fichier : tout mode
                // autre que « template » (Aplati, qui ne produit qu'une image de fond)
                // doit rapporter des éléments.
                if (data.mode !== 'template' && (!data.elements || data.elements.length === 0)) emptyExtract++;
                tabsData.push(buildPdfProjectData(file, data, skeleton, pdfTemplate?.template?.canvas));
            } catch (err) {
                if (err.code === 'ABORTED' || err.name === 'AbortError') { cancelled = true; break; }
                // Avant, une session expirée sortait de la fonction par un `return` :
                // les PDF déjà convertis étaient jetés sans onglet, et `pdfTemplate`
                // restait chargé pour l'import suivant. On sort de la BOUCLE, comme
                // pour l'annulation, et la fin de fonction fait le ménage.
                if (err.code === 'SESSION_EXPIRED') { sessionExpired = true; break; }
                errors.push({ name: file.name, message: err.message || 'Erreur' });
            }
        }
        // Les fichiers déjà convertis avant l'annulation (ou l'expiration de session)
        // sont conservés : les jeter reviendrait à punir l'utilisateur d'avoir
        // interrompu la suite.
        if (tabsData.length > 0) useTabsStore.getState().addTabsBatch(tabsData, { activateFirst: true });
        importAbortRef.current = null;
        setImportStatus(null); setPdfPendingFiles([]); setPdfTemplate(null);

        const parts = [`${tabsData.length}/${files.length} fichiers importés`];
        if (cancelled) parts.push('import interrompu');
        if (sessionExpired) parts.push('session expirée — reconnectez-vous pour importer le reste');
        if (skeleton.length) parts.push('modèle appliqué');
        if (emptyExtract) parts.push(`${emptyExtract} sans extraction`);
        if (errors.length) parts.push(`${errors.length} échec(s) : ${errors.map(e => e.name).join(', ')}`);
        showRegToast(sessionExpired ? 'err' : (errors.length || cancelled ? 'warn' : 'ok'), parts.join(' · '));
    };

    // ─── Modèle réglementaire (.lfgr) ──────────────────────────────────
    // Un modèle capture le PLACEMENT + la MISE EN FORME (+ texte de départ comme
    // guide) des calques réglementaires d'une étiquette de référence. On peut
    // ensuite l'appliquer à une autre étiquette : le texte/contenu courant est
    // conservé, seuls position et style du modèle sont repris.
    const ROLE_LABELS_FR = {
        signal_word: "mention d'avertissement",
        hazard: 'mentions H',
        euh: 'mentions EUH',
        contains: 'contient',
        precaution: 'mentions P',
        precaution_usage: "précautions d'usage",
        health_warning: 'avertissement sanitaire',
        statements: 'mentions imposées',
        leaflet: 'notice nicotine',
        nicotine_salt: 'sels de nicotine',
        supplementary: 'mentions complémentaires',
        notice: 'notices',
        composition: 'composition',
        distributor: 'distributeur',
        volume: 'volume',
        nicotine: 'nicotine',
        pg: 'PG',
        vg: 'VG',
        ufi: 'UFI',
    };
    const humanRoleKey = (key) => {
        if (!key) return '';
        if (key.startsWith('pictogram::')) {
            const code = key.slice('pictogram::'.length);
            return code ? `pictogramme ${code}` : 'pictogramme';
        }
        // Un exemplaire d'avertissement sanitaire (« 1.2 » = avertissement 1, copie 2).
        if (key.startsWith('health_warning::')) {
            const [av, copie] = key.slice('health_warning::'.length).split('.');
            return `avertissement sanitaire${av && av !== '1' ? ` ${av}` : ''}${copie ? `, exemplaire ${copie}` : ''}`;
        }
        return ROLE_LABELS_FR[key] || key;
    };
    const _hasRegLayers = () => (elements || []).some(e => e && e.regBlock);

    const handleSaveRegulatoryTemplate = async () => {
        if (!_hasRegLayers()) {
            showRegToast('warn', "Aucun calque réglementaire sur cette étiquette. Importez d'abord une fiche (UFI / client).");
            return;
        }
        const tpl = extractRegulatoryTemplate(elements, canvas);
        tpl.createdAt = new Date().toISOString();
        const json = JSON.stringify(tpl, null, 2);
        const baseName = `${_buildProjectFilename()}_modele`;
        try {
            if (window.showSaveFilePicker) {
                const handle = await window.showSaveFilePicker({
                    suggestedName: `${baseName}.lfgr`,
                    types: [{ description: 'Modèle réglementaire LabelForge', accept: { 'application/json': ['.lfgr'] } }],
                });
                const writable = await handle.createWritable();
                await writable.write(json);
                await writable.close();
            } else {
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(json);
                const a = document.createElement('a');
                a.href = dataStr;
                a.download = `${baseName}.lfgr`;
                a.click();
            }
            const n = tpl.layers.length;
            // Deux cas où le modèle ne peut pas tout garder. Les taire, c'est
            // laisser croire qu'il restituera à l'identique.
            const reserves = [];
            if (tpl._stylesMixtes?.length) {
                reserves.push(`style non uniforme sur ${tpl._stylesMixtes.length} calque(s) — ${tpl._stylesMixtes.map(humanRoleKey).join(', ')} : le modèle garde la mise en forme telle quelle, sans pouvoir la réappliquer`);
            }
            if (tpl._clesEnDouble?.length) {
                reserves.push(`rôle(s) en double — ${tpl._clesEnDouble.map(humanRoleKey).join(', ')} : chaque exemplaire garde son placement, dans l'ordre où ils sont empilés`);
            }
            showRegToast(reserves.length ? 'warn' : 'ok',
                [`Modèle réglementaire enregistré · ${n} calque${n > 1 ? 's' : ''} (placement + mise en forme)`, ...reserves].join(' · ') + '.');
        } catch (err) {
            if (err.name !== 'AbortError') showRegToast('err', "Échec de l'enregistrement du modèle : " + err.message);
        }
    };

    /**
     * Applique un modèle déjà chargé (et, le cas échéant, déjà mis à l'échelle).
     * Séparée du choix de fichier pour être rejouable après la modale d'échelle.
     */
    const appliquerModeleReglementaire = (tpl, mention = '') => {
        const {
            patches, creations, applied, missingInLabel, adoptes, nonPlaces,
            sansGabarit, stylesNonRejoues,
        } = applyRegulatoryTemplate(tpl, elements);

        if (applied === 0 && (!creations || creations.length === 0)) {
            showRegToast('warn', "Aucun calque correspondant : les rôles du modèle ne correspondent pas à ceux de cette étiquette. Vérifiez que le modèle a été enregistré depuis une étiquette du même type.");
            return;
        }
        patchElements(patches);

        // Exemplaires que le modèle porte en plus : l'utilisateur avait dupliqué
        // un calque (l'UFI répété sur un rabat) au moment d'enregistrer son
        // modèle. Le modèle le REFAIT ici, avec le texte du produit courant.
        const idsCrees = [];
        for (const calque of (creations || [])) {
            const id = crypto.randomUUID();
            idsCrees.push(id);
            addElement({ ...calque, id });
        }

        // Le modèle repose le placement et la mise en forme ; le texte, lui,
        // est celui de l'étiquette courante et n'a aucune raison de tenir dans
        // le cadre du modèle. On resserre donc les cadres sur leur contenu —
        // les glyphes ne bougent pas, seul le cadre les épouse (cf. fitFrame).
        fitElementsToContent([...Object.keys(patches), ...idsCrees],
            { nomHistorique: 'Modèle réglementaire · ajusté au contenu' }).catch(() => {});

        const parts = [];
        if (applied) parts.push(`${applied} calque${applied > 1 ? 's' : ''} replacé${applied > 1 ? 's' : ''} & reformaté${applied > 1 ? 's' : ''}`);
        if (idsCrees.length) parts.push(`${idsCrees.length} exemplaire${idsCrees.length > 1 ? 's' : ''} recréé${idsCrees.length > 1 ? 's' : ''} (calque${idsCrees.length > 1 ? 's' : ''} dupliqué${idsCrees.length > 1 ? 's' : ''} dans le modèle)`);
        if (mention) parts.push(mention);
        if (missingInLabel.length) parts.push(`${missingInLabel.length} rôle(s) du modèle absent(s) ici : ${missingInLabel.map(humanRoleKey).join(', ')}`);
        // Mentions que le modèle ne connaissait pas — une fiche plus récente en
        // apporte, un autre produit n'a pas les mêmes pictogrammes. Elles sont
        // rangées dans le bloc, mais cette place-là vient du logiciel et pas du
        // modèle : le dire, et demander un coup d'œil.
        if (adoptes?.length) parts.push(`${adoptes.length} mention(s) que le modèle ne connaissait pas, alignée(s) sur la mise en forme du bloc et rangée(s) à sa suite : ${adoptes.map(humanRoleKey).join(', ')} — VÉRIFIEZ leur place`);
        if (nonPlaces?.length) parts.push(`${nonPlaces.length} rôle(s) sans gabarit, laissé(s) tel quel : ${nonPlaces.map(humanRoleKey).join(', ')}`);
        if (sansGabarit?.length) parts.push(`plus d'exemplaires ici que dans le modèle : ${sansGabarit.map(humanRoleKey).join(', ')} — les surnuméraires n'ont pas bougé`);
        // Mise en forme mot à mot que le nouveau texte ne permet pas de rejouer.
        if (stylesNonRejoues?.length) {
            parts.push(`mise en forme mot à mot non rejouable sur un texte différent (${stylesNonRejoues.map(humanRoleKey).join(', ')}) — la couleur majoritaire a été appliquée`);
        }
        // Mentions posées dans une couleur que rien ne distingue du fond. Le
        // modèle a fait son travail — placement et couleur fidèles — mais si
        // l'étiquette de référence portait ses mentions en blanc sur un aplat
        // noir et que celle-ci n'a pas cet aplat, elles sont INVISIBLES. Elles
        // partiront pourtant au PDF, et l'utilisateur conclura que le modèle ne
        // les a pas posées. (Retour utilisateur du 2026-08-28 : trois mentions
        // signalées « absentes » étaient à leur place, en #ffffff sur blanc.)
        const apresModele = elements.map((el) => (patches[el.id] ? { ...el, ...patches[el.id] } : el));
        const illisibles = mentionsIllisibles(
            [...apresModele.filter((el) => patches[el.id]), ...(creations || [])],
            canvas,
            apresModele,
        );
        if (illisibles.length) {
            const detail = [...new Set(illisibles.map((m) => `${humanRoleKey(m.key)} en ${m.couleur}`))].join(', ');
            parts.push(`${illisibles.length} mention(s) INVISIBLE(S) sur ce fond — ${detail} : le modèle a reposé la couleur de l'étiquette de référence, dont le fond était différent. Recolorez-les, ou posez l'aplat manquant`);
        }

        // Calques hors du plan de travail : l'atelier ne les rogne pas, l'export
        // si. Un PDF sans mentions légales annoncé comme réussi, c'est le pire
        // des silences — on le dit, chiffres à l'appui.
        const horsPlan = Object.entries(patches).filter(([id, p]) => {
            const el = elements.find((x) => x.id === id);
            if (!el) return false;
            const x = p.x ?? el.x, y = p.y ?? el.y;
            const w = p.width ?? el.width, h = p.height ?? el.height;
            return x < -0.5 || y < -0.5
                || x + w > canvas.width_mm + 0.5 || y + h > canvas.height_mm + 0.5;
        }).length;
        if (horsPlan) parts.push(`${horsPlan} calque(s) hors du plan de travail — ils seront COUPÉS à l'export`);

        // Une mention illisible est aussi grave qu'une mention coupée : même
        // niveau d'alerte, pas un simple avertissement.
        showRegToast((horsPlan || illisibles.length) ? 'err'
            : (missingInLabel.length || adoptes?.length || nonPlaces?.length
               || sansGabarit?.length || stylesNonRejoues?.length) ? 'warn' : 'ok',
            parts.join(' · '));
    };

    // Modèle chargé qui attend un choix d'échelle (format différent).
    const [modeleAEchelle, setModeleAEchelle] = React.useState(null);
    const [isRegulatoryOpen, setIsRegulatoryOpen] = React.useState(false);

    const handleApplyRegulatoryTemplate = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!_hasRegLayers()) {
            showRegToast('warn', "Aucun calque réglementaire à reformater. Importez d'abord une fiche (UFI / client) sur cette étiquette.");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            let tpl;
            try { tpl = JSON.parse(ev.target.result); }
            catch (err) { showRegToast('err', "Fichier .lfgr illisible (JSON invalide)."); return; }
            if (!isRegulatoryTemplate(tpl)) {
                showRegToast('err', "Ce fichier n'est pas un modèle réglementaire (.lfgr) valide.");
                return;
            }
            // Format différent : on ne décide pas à la place de l'utilisateur.
            // Réduire un corps de texte réglementaire peut le rendre non
            // conforme ; l'appliquer tel quel peut le sortir du plan de travail.
            if (formatDifferent(tpl, canvas)) {
                setModeleAEchelle({
                    tpl,
                    nom: file.name.replace(/\.(lfgr|json)$/i, ''),
                    formatModele: tpl.canvas,
                    formatCible: { width_mm: canvas.width_mm, height_mm: canvas.height_mm },
                });
                return;
            }
            appliquerModeleReglementaire(tpl);
        };
        reader.readAsText(file);
    };

    const choisirEchelleModele = (choix) => {
        const demande = modeleAEchelle;
        setModeleAEchelle(null);
        if (!demande) return;
        if (choix === 'tel-quel') {
            appliquerModeleReglementaire(demande.tpl,
                `positions du modèle reprises telles quelles (${demande.formatModele.width_mm}×${demande.formatModele.height_mm} mm)`);
            return;
        }
        const { template, pictosSousMinimum, textesTropPetits } = mettreALEchelleModele(
            demande.tpl, canvas, { adapterCorps: choix === 'tout' }
        );
        const mentions = [choix === 'tout'
            ? `mis à l'échelle de ${canvas.width_mm}×${canvas.height_mm} mm, corps de texte compris`
            : `positions mises à l'échelle, corps de texte inchangés`];
        if (pictosSousMinimum.length) {
            mentions.push(`ATTENTION : pictogramme(s) ${pictosSousMinimum.join(', ')} sous les ${PICTO_MIN_MM} mm réglementaires`);
        }
        if (textesTropPetits?.length) {
            mentions.push(`ATTENTION : ${textesTropPetits.map(humanRoleKey).join(', ')} sous ${CORPS_MIN_PT} pt — vérifiez la taille minimale exigée pour ce conditionnement`);
        }
        appliquerModeleReglementaire(template, mentions.join(' · '));
    };

    // Sections du menu « Fichier » — regroupe les actions fichier/partage/import
    // qui encombraient la barre. Chaque entrée déclenche l'action/modale existante.
    const fileSections = [
        {
            title: 'Projet',
            items: [
                { label: 'Nouveau projet', onClick: onOpenNew, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                ) },
                { label: 'Ouvrir…', onClick: () => fileInputRef.current?.click(), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                ) },
                { label: 'Enregistrer', onClick: handleSaveProject, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                ) },
            ],
        },
        {
            title: 'Documentation',
            items: [
                // Deux destinations, deux régimes : la bibliothèque interne est
                // derrière la connexion, les documents publiés ne le sont pas.
                { label: 'Rapports de séance', onClick: () => window.open('/rapports', '_blank', 'noopener'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                ) },
                { label: 'Documents publiés', onClick: () => window.open('/docs/', '_blank', 'noopener'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><line x1="3" y1="12" x2="21" y2="12"/><path d="M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z"/></svg>
                ) },
            ],
        },
        {
            title: 'Aperçu & partage',
            items: [
                { label: 'Mode présentation', onClick: () => window.dispatchEvent(new CustomEvent('labelforge:presentation')), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"/><path d="M8 21h8"/><path d="M12 17v4"/><polygon points="10 9 15 12 10 15 10 9"/></svg>
                ) },
                { label: 'Partager (lien public)', onClick: onOpenShare, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                ) },
            ],
        },
        {
            title: 'Importer',
            items: [
                { label: 'Importer des PDF…', onClick: () => pdfInputRef.current?.click(), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="11" x2="12" y2="17"/><polyline points="9 14 12 17 15 14"/></svg>
                ) },
                { label: 'Importer une base CSV', onClick: () => csvInputRef.current?.click(), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
                ) },
                { label: 'Fiche réglementaire (UFI)', onClick: () => setIsRegulatoryOpen(true), accent: true, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                ) },
            ],
        },
        {
            title: 'Modèle réglementaire',
            items: [
                { label: 'Enregistrer comme modèle (.lfgr)', onClick: handleSaveRegulatoryTemplate, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="7" x="3" y="3" rx="1"/><rect width="9" height="7" x="3" y="14" rx="1"/><rect width="5" height="7" x="16" y="14" rx="1"/></svg>
                ) },
                { label: 'Appliquer un modèle (.lfgr)…', onClick: () => lfgrInputRef.current?.click(), accent: true, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"/><path d="M19.27 13.73A2.5 2.5 0 0 0 17.5 13h-11A2.5 2.5 0 0 0 4 15.5V17a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1.5c0-.66-.26-1.3-.73-1.77Z"/><path d="M14 13V8.5C14 7 15 7 15 5a3 3 0 0 0-3-3 3 3 0 0 0-3 3c0 2 1 2 1 3.5V13"/></svg>
                ) },
            ],
        },
        {
            title: 'Exporter',
            items: [
                { label: 'Exporter (PDF, PNG, SVG)…', onClick: onOpenExport, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                ) },
            ],
        },
        // Laboratoire : outils internes en bêta. Studio I.A Beta : rôle admin seulement ;
        // Pilotage des agents : admin, ou droit « pilotage » à part (acces_pilotage, rendu par
        // /api/auth/me — le compte de Guillaume). Le backend refuse de toute façon ces routes aux autres.
        ...(user?.role === 'admin' || user?.acces_pilotage ? [{
            title: 'Laboratoire',
            items: [
                ...(user?.role === 'admin' ? [{ label: 'Studio I.A Beta', onClick: () => window.open('/studio-ia-beta', '_blank', 'noopener'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6"/><path d="M10 3v6l-5 9a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-9V3"/><path d="M7.5 15h9"/></svg>
                ) }] : []),
                { label: 'Pilotage des agents', onClick: () => window.open('/pilotage', '_blank', 'noopener'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="2.5"/><circle cx="19" cy="6" r="2.5"/><circle cx="19" cy="18" r="2.5"/><path d="M7.3 11l9.4-4M7.3 13l9.4 4"/></svg>
                ) },
            ],
        }] : []),
    ];

    // Tracé de découpe : posé au premier plan, verrouillé, puis sélectionné pour
    // que l'inspecteur montre ses réglages. S'il en existe déjà un, il est
    // seulement sélectionné : une découpe importée ne se remplace pas d'un clic.
    const onAddDecoupe = () => {
        const r = useLabelStore.getState().ajouterDecoupe();
        showRegToast(r.cree ? 'ok' : 'warn', r.cree
            ? 'Tracé de découpe ajouté au premier plan, verrouillé. Ses réglages sont dans l\'inspecteur.'
            : 'Ce document a déjà un tracé de découpe : il est sélectionné, ses réglages sont dans l\'inspecteur.');
    };

    // Sections du menu « + Ajout » — déclencheurs déplacés depuis la colonne de
    // gauche (place rendue à la liste des calques). Mêmes actions qu'avant,
    // l'Inspecteur ne change pas.
    const addSections = [
        {
            title: 'Éléments',
            items: [
                { label: 'Texte', onClick: onAddText, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
                ) },
                { label: 'Image…', onClick: onAddImage, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                ) },
                { label: 'Code-barres', onClick: onAddBarcode, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5v14M8 5v14M12 5v14M16 5v14M21 5v14"/></svg>
                ) },
            ],
        },
        {
            title: 'Formes vectorielles',
            items: [
                { label: 'Carré', onClick: () => onAddShape('rect'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                ) },
                { label: 'Rond', onClick: () => onAddShape('circle'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9"/></svg>
                ) },
                { label: 'Rectangle arrondi', onClick: () => onAddShape('rounded-rect'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="6"/></svg>
                ) },
                { label: 'Ligne / Trait', onClick: () => onAddShape('line'), icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/></svg>
                ) },
                { label: 'Bibliothèque de formes…', onClick: onOpenShapeLibrary, accent: true, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                ) },
            ],
        },
        {
            title: 'Finition',
            items: [
                { label: 'Tracé de découpe (CutContour)', onClick: onAddDecoupe, icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="3 2"><rect x="3" y="3" width="18" height="18" rx="3"/></svg>
                ) },
            ],
        },
    ];

    return (
        // z-[90] : au-dessus des règles du canvas (z-[80]) pour que les menus
        // déroulants (Fichier / Ajout / Historique) ne passent pas dessous,
        // et sous les modales (z-[100]).
        <div ref={toolbarRef} className="lf-toolbar bg-forge-bg border-b border-white/10 py-2 px-4 flex items-center justify-between text-[11px] font-mono shrink-0 relative z-[90] w-full transition-all shadow-sm">

            {/* GAUCHE : Menus Fichier / Ajout / Historique + état de sauvegarde */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <FileMenu sections={fileSections} />
                <FileMenu
                    label="Ajout"
                    sections={addSections}
                    icon={(
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    )}
                />
                <HistoryMenu />

                {/* Palette d'outils : le bouton reste visible en permanence et
                    reflète l'état. C'est la garantie qu'une palette refermée
                    par mégarde se retrouve toujours — le raccourci Tab ne peut
                    pas être la seule porte d'entrée. */}
                <button
                    type="button"
                    onClick={() => useLabelStore.getState().setToolPalette({ open: !toolPaletteOpen })}
                    title={toolPaletteOpen ? 'Masquer la palette d\'outils (Tab)' : 'Afficher la palette d\'outils (Tab)'}
                    aria-pressed={toolPaletteOpen}
                    className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border transition-colors ${
                        toolPaletteOpen
                            ? 'bg-white/10 border-white/15 text-[rgba(var(--forge-text),1)]'
                            : 'bg-transparent border-transparent text-[rgba(var(--forge-muted),0.6)] hover:text-[rgba(var(--forge-text),1)] hover:bg-white/5'
                    }`}
                    aria-label={toolPaletteOpen ? "Masquer la palette d'outils" : "Afficher la palette d'outils"}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                </button>

                {/* Inputs fichiers cachés — déclenchés depuis le menu Fichier */}
                <input type="file" accept=".lfg,.json,application/json" ref={fileInputRef} onChange={handleLoadProject} className="hidden" />
                <input type="file" accept=".pdf" multiple ref={pdfInputRef} onChange={handleImportPDF} className="hidden" />
                <input type="file" accept=".lfgr,.json,application/json" data-entree="modele-sur-pdf" ref={pdfTemplateInputRef} onChange={handlePickPdfTemplate} className="hidden" />
                <input type="file" accept=".csv" ref={csvInputRef} onChange={handleImportCSV} className="hidden" />
                <input type="file" accept=".lfgr,.json,application/json" data-entree="modele-a-appliquer" ref={lfgrInputRef} onChange={handleApplyRegulatoryTemplate} className="hidden" />
                {/* Les deux modales réglementaires vivent ICI, et non dans
                    EditorView : leur compte rendu passe par `showRegToast`, qui
                    est local à cette barre. Rendues plus haut, elles recevaient
                    une fonction hors de portée. */}
                <RegulatoryImportModal isOpen={isRegulatoryOpen} onClose={() => setIsRegulatoryOpen(false)} onDone={showRegToast} />
                <RegulatoryTemplateScaleModal demande={modeleAEchelle} onChoisir={choisirEchelleModele} onAnnuler={() => setModeleAEchelle(null)} />
            </div>

            {/* CENTRE : Studio I.A + FDS Studio (centrés via flex 3 colonnes — pas
                d'absolu pour éviter de chevaucher les zones gauche/droite). */}
            <div className="flex items-center justify-center shrink-0 z-10 px-2 gap-2">
                <HelpTarget id="toolbar.studio-ia"><button
                    onClick={onOpenInspiration}
                    className="px-5 py-2 bg-forge-accent text-white hover:bg-forge-accent-hover rounded-full transition-all flex items-center gap-2 whitespace-nowrap shadow-[0_0_18px_rgba(var(--forge-accent),0.35)] hover:shadow-[0_0_28px_rgba(var(--forge-accent),0.55)] hover:scale-[1.02] border border-white/10"
                    title="Studio I.A"
                >
                    {(aiStatus === 'running' || aiStatus === 'queued' || isGeneratingLocally) ? (
                        <span className="font-bold tracking-widest animate-pulse">↻ {Math.round(aiProgress * 100)}%</span>
                    ) : (
                        <>
                            <img src={polyprintP} alt="" className="h-[18px] w-auto" draggable="false" />
                            <span className="font-bold tracking-widest uppercase text-[11px]">Studio I.A</span>
                        </>
                    )}
                </button></HelpTarget>
                {/* FDS Studio : bouton retiré du header (2026-07-14, demande client) —
                    la fonctionnalité reste accessible via l'onglet FDS Studio du
                    panneau latéral (plus bas dans ce fichier). */}
                {/* Analyse PDF — préflight / contrôle prépresse (couleur indigo) */}
                <button
                    onClick={onOpenPreflight}
                    className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-500 rounded-full transition-all flex items-center gap-2 whitespace-nowrap shadow-[0_0_14px_rgba(99,102,241,0.35)] hover:shadow-[0_0_24px_rgba(99,102,241,0.55)] hover:scale-[1.02] border border-white/10"
                    title="Analyse PDF — contrôle prépresse (préflight)" aria-label="Analyse PDF — contrôle prépresse (préflight)"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><circle cx="11" cy="14" r="2.4" /><line x1="12.8" y1="15.8" x2="15" y2="18" />
                    </svg>
                    <span className="font-bold tracking-widest uppercase text-[11px]">Analyse PDF</span>
                </button>
            </div>
            
            {/* DROITE : Présentation / Export / Préférences / Profil */}
            <div className="flex items-center gap-3 flex-1 justify-end min-w-0">
                
                {/* Imports PDF / CSV déplacés dans le menu « Fichier » (section Importer).
                    Les <input> cachés correspondants sont dans la zone gauche. */}

                {/* Bouton réparateur — apparaît uniquement quand on détecte des pictos
                    invisibles (color quasi-identique au fond du canvas). Disparaît dès
                    que le clic les a tous repassés à la bonne couleur de contraste. */}
                {invisibleShapeIds.length > 0 && (
                    <button
                        onClick={repairInvisibleShapes}
                        title={`${invisibleShapeIds.length} picto(s) probablement invisible(s) sur ce fond. Cliquez pour leur appliquer une couleur de contraste.`}
                        aria-label={`${invisibleShapeIds.length} picto(s) probablement invisible(s) sur ce fond. Cliquez pour leur appliquer une couleur de contraste.`}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider rounded-full border bg-amber-500/15 border-amber-400/40 text-amber-300 hover:bg-amber-500/25 hover:border-amber-400/60 hover:text-amber-100 transition-all animate-pulse"
                    >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                        </svg>
                        Réparer {invisibleShapeIds.length} picto{invisibleShapeIds.length > 1 ? 's' : ''}
                    </button>
                )}

                {/* Présentation + Export + Partage déplacés (présentation/partage →
                    sidebar gauche ; export → menu Fichier). La barre reste épurée. */}

                {/* Concours d'univers — la jauge du jour, pour les seuls participants
                    (rien pour les autres comptes). */}
                <JaugeConcours />

                {/* Sauvegarde automatique — Zustand persist écrit en continu dans
                    IndexedDB. Pulse pendant la propagation, puis stable.
                    Placé à DROITE, contre la roue dentée : c'est un voyant d'état,
                    pas une commande. À gauche il s'intercalait entre les menus et
                    le Studio I.A, dans la zone la plus disputée de la barre. */}
                <div
                    title={saveState === 'saving'
                        ? "Sauvegarde automatique en local…"
                        : "Auto-sauvegardé en local (jamais perdu). Pour un fichier : Fichier ▸ Enregistrer (.lfg). L'astérisque rouge d'un onglet = modifs non encore enregistrées dans un fichier."}
                    className="lf-autosave flex shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/15 border border-white/5"
                >
                    <span className={`w-1.5 h-1.5 rounded-full transition-all ${
                        saveState === 'saving'
                            ? 'bg-amber-400 animate-pulse shadow-[0_0_6px_rgba(251,191,36,0.6)]'
                            : 'bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.4)]'
                    }`} />
                    <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">
                        {saveState === 'saving' ? 'Auto…' : 'Auto ✓'}
                    </span>
                </div>

                {/* Préférences (avant le profil) */}
                <HelpTarget id="toolbar.preferences"><button onClick={() => window.dispatchEvent(new CustomEvent('open-preferences'))} className="ml-2 p-2 text-white/40 hover:text-white transition-all rounded-full hover:bg-white/10 hover:rotate-90" title="Préférences Globales" aria-label="Préférences Globales">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                </button></HelpTarget>

                {/* Guide — entre les préférences et le profil, là où l'œil cherche
                    l'aide dans la plupart des logiciels. Interrupteur : un second
                    appui referme le panneau. Le point d'interrogation est cerclé
                    pour se distinguer des icônes de trait voisines. */}
                <button
                    onClick={() => useAIStudioStore.getState().toggleHelp()}
                    title="Guide d'utilisation"
                    aria-label="Guide d'utilisation"
                    className={`ml-2 w-9 h-9 flex items-center justify-center rounded-full border transition-all ${
                        helpOuvert
                            ? 'text-[rgba(var(--forge-sky),1)] border-[rgba(var(--forge-sky),0.4)] bg-[rgba(var(--forge-sky),0.14)]'
                            : 'text-white/40 border-transparent hover:text-[rgba(var(--forge-sky),1)] hover:bg-white/10'
                    }`}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9.5" />
                        <path d="M9.6 9.2a2.5 2.5 0 1 1 3.4 2.3c-.72.3-1 .92-1 1.63v.37" />
                        <line x1="12" y1="17" x2="12" y2="17.01" />
                    </svg>
                </button>

                {/* Bouton profil utilisateur */}
                <button
                    onClick={onOpenProfile}
                    title="Mon profil"
                    className="ml-2 flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-black/40 border border-white/5 hover:bg-black/60 hover:border-white/15 transition-colors shadow-inner"
                >
                    {user?.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-[10px] font-bold text-white">
                            {(user?.username || user?.email || '?').charAt(0).toUpperCase()}
                        </div>
                    )}
                    <span className="text-[11px] font-mono text-white/80 max-w-[120px] truncate">
                        {user?.username || user?.email?.split('@')[0] || 'Utilisateur'}
                    </span>
                </button>
            </div>

            {/* Modale d'import PDF — multi-fichiers + modèle réglementaire optionnel */}
            {pdfPendingFiles.length > 0 && createPortal(
                <div className="fixed inset-0 z-[140] flex items-center justify-center modal-backdrop-strong backdrop-blur-sm p-6 font-mono">
                    <div className="bg-forge-bg/95 backdrop-blur-md border border-forge-border/25 w-full max-w-[560px] rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.5)] overflow-hidden">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-white/5 bg-black/20 flex items-center justify-between">
                            <div>
                                <h2 className="text-white text-[13px] font-bold tracking-[0.2em] uppercase">
                                    Importer {pdfPendingFiles.length > 1 ? `${pdfPendingFiles.length} PDF` : 'un PDF'}
                                </h2>
                                <p className="text-[10px] text-white/40 mt-1 truncate max-w-[440px]">
                                    {pdfPendingFiles.length === 1 ? pdfPendingFiles[0].name : `${pdfPendingFiles.length} fichiers · un onglet par fichier`}
                                </p>
                            </div>
                            {!importStatus && (
                                <button
                                    onClick={() => { setPdfPendingFiles([]); setPdfTemplate(null); }}
                                    className="w-7 h-7 rounded-md bg-black/30 border border-white/10 text-white/50 hover:text-forge-rose hover:border-forge-rose/30 flex items-center justify-center transition-colors"
                                    title="Annuler" aria-label="Annuler"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                            )}
                        </div>

                        {importStatus ? (
                            /* ── Progression de l'import (1 fichier = barre indéterminée, N = avancement réel) ── */
                            <div className="p-7 flex flex-col items-center gap-4">
                                <div className="flex items-center gap-2.5 text-orange-500">
                                    <span className="w-4 h-4 rounded-full border-2 border-orange-500/25 border-t-orange-500 animate-spin" aria-hidden="true" />
                                    <span className="text-[11px] font-mono font-bold tracking-[0.18em] uppercase">
                                        {importStatus.phase === 'upload' ? 'Envoi du fichier' : 'Import en cours'}
                                    </span>
                                </div>

                                <div className="w-full">
                                    <div className="flex justify-between items-baseline text-[10px] font-mono text-white/60 mb-1.5">
                                        <span>
                                            Mode <strong className="text-white/85">{PDF_MODE_LABELS[importStatus.mode] || importStatus.mode}</strong>
                                            {importStatus.total > 1 && <> · fichier {importStatus.current}/{importStatus.total}</>}
                                        </span>
                                        <span className="tabular-nums text-white/45">
                                            {importStatus.phase === 'upload'
                                                ? `${Math.round((importStatus.upload || 0) * 100)} %`
                                                : importElapsed < 60 ? `${importElapsed} s` : `${Math.floor(importElapsed / 60)} min ${String(importElapsed % 60).padStart(2, '0')} s`}
                                        </span>
                                    </div>
                                    {/* Pendant l'envoi vers le stockage, on connaît l'avancement réel : on l'affiche.
                                        Ensuite le serveur travaille sans rendre de compte → barre indéterminée. */}
                                    {importStatus.phase === 'upload' ? (
                                        <div className="h-2 rounded-full bg-black/40 overflow-hidden">
                                            <div className="h-full bg-orange-500 transition-all duration-150" style={{ width: `${Math.round((importStatus.upload || 0) * 100)}%` }} />
                                        </div>
                                    ) : importStatus.total > 1 ? (
                                        <div className="h-2 rounded-full bg-black/40 overflow-hidden">
                                            <div className="h-full bg-orange-500 transition-all duration-200" style={{ width: `${Math.round((importStatus.current / importStatus.total) * 100)}%` }} />
                                        </div>
                                    ) : (
                                        <div className="h-2 rounded-full bg-black/40 lf-bar-indeterminate text-orange-500" role="progressbar" aria-label="Import du PDF en cours" />
                                    )}
                                </div>

                                <p className="text-[9.5px] leading-relaxed text-white/45 text-center max-w-[420px]">
                                    {PDF_MODE_HINTS[importStatus.mode] || 'Le PDF est traité côté serveur.'}
                                    {importStatus.total > 1 && ' Chaque fichier est ouvert dans son propre onglet.'}
                                </p>
                                {importElapsed >= 25 && (
                                    <p className="text-[9.5px] font-mono text-orange-500/70 text-center">
                                        Toujours en cours — ne fermez pas l'onglet du navigateur.
                                    </p>
                                )}
                                {/* Une issue, toujours : 100 Mo sur une ligne d'atelier, c'est plusieurs
                                    minutes d'envoi, et se tromper de mode ne doit pas obliger à recharger. */}
                                <button
                                    onClick={cancelPdfImport}
                                    className="text-[9px] font-mono uppercase tracking-[0.18em] text-white/35 hover:text-forge-rose transition-colors"
                                >
                                    {importStatus.phase === 'upload' ? "Annuler l'envoi" : "Annuler l'import"}
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Choix du modèle réglementaire (optionnel) */}
                                <div className="px-5 pt-4">
                                    {pdfTemplate ? (
                                        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-orange-500/15 border border-orange-500/40">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-orange-500 shrink-0"><rect width="18" height="7" x="3" y="3" rx="1"/><rect width="9" height="7" x="3" y="14" rx="1"/><rect width="5" height="7" x="16" y="14" rx="1"/></svg>
                                                <span className="text-[10px] font-mono text-white/80 truncate">Modèle : <strong>{pdfTemplate.name}</strong></span>
                                            </div>
                                            <button onClick={() => setPdfTemplate(null)} className="text-[9px] font-mono uppercase tracking-wider text-white/40 hover:text-forge-rose shrink-0">Retirer</button>
                                        </div>
                                    ) : (
                                        <button onClick={() => pdfTemplateInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-dashed border-white/15 text-white/55 hover:text-white hover:border-orange-500/40 transition-all">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect width="18" height="7" x="3" y="3" rx="1"/><rect width="9" height="7" x="3" y="14" rx="1"/><rect width="5" height="7" x="16" y="14" rx="1"/></svg>
                                            <span className="text-[10px] font-mono uppercase tracking-wider">Appliquer un modèle (.lfgr) — optionnel</span>
                                        </button>
                                    )}
                                </div>

                                {/* Body : 2 cartes (mode appliqué à TOUS les fichiers) */}
                                <div className="p-5 grid grid-cols-3 gap-3">
                                    <button
                                        onClick={() => executePdfImport('vector')}
                                        className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-emerald-500/5 border border-emerald-400/30 ring-1 ring-emerald-400/15 hover:bg-emerald-500/15 hover:border-emerald-400/50 transition-all text-left"
                                    >
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-emerald-400 group-hover:scale-110 transition-transform"><path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="m2 17 10 5 10-5"/><path d="m2 12 10 5 10-5"/></svg>
                                        <span className="text-[11px] font-bold text-white tracking-widest uppercase">Fidèle + calques</span>
                                        <span className="text-[9px] text-white/50 leading-relaxed text-center">Rendu fidèle (couleurs, dégradés, transparence) + texte & découpe éditables, en calques</span>
                                    </button>
                                    <button
                                        onClick={() => executePdfImport('extract')}
                                        className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-black/30 border border-white/10 hover:bg-blue-500/10 hover:border-blue-400/40 transition-all text-left"
                                    >
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-blue-400 group-hover:scale-110 transition-transform"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                                        <span className="text-[11px] font-bold text-white tracking-widest uppercase">Fond fidèle</span>
                                        <span className="text-[9px] text-white/50 leading-relaxed text-center">Fond raster fidèle + textes éditables par-dessus</span>
                                    </button>
                                    <button
                                        onClick={() => executePdfImport('template')}
                                        className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-black/30 border border-white/10 hover:bg-purple-500/10 hover:border-purple-400/40 transition-all text-left"
                                    >
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-purple-400 group-hover:scale-110 transition-transform"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/></svg>
                                        <span className="text-[11px] font-bold text-white tracking-widest uppercase">Aplati</span>
                                        <span className="text-[9px] text-white/50 leading-relaxed text-center">Fusionne le PDF en une image gabarit verrouillée au fond</span>
                                    </button>
                                </div>

                                {/* Footer */}
                                <div className="px-5 pb-5">
                                    <button
                                        onClick={() => { setPdfPendingFiles([]); setPdfTemplate(null); }}
                                        className="w-full py-2 rounded-lg bg-black/30 border border-white/5 text-white/50 hover:text-white hover:bg-black/40 text-[10px] font-bold tracking-widest uppercase transition-colors"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {/* Toast non-bloquant — retour d'application/enregistrement d'un modèle réglementaire */}
            {regToast && createPortal(
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[140] w-full max-w-[600px] px-4 pointer-events-none">
                    <div className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl border shadow-[0_15px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl font-mono text-[11px] leading-snug ${
                        regToast.type === 'ok' ? 'bg-green-500/15 border-green-500/40 text-green-100'
                        : regToast.type === 'warn' ? 'bg-amber-500/15 border-amber-500/40 text-amber-100'
                        : 'bg-red-500/15 border-red-500/40 text-red-100'
                    }`}>
                        <span className="mt-0.5 shrink-0">{regToast.type === 'ok' ? '✓' : regToast.type === 'warn' ? '⚠' : '✕'}</span>
                        <span className="flex-1">{regToast.text}</span>
                        <button onClick={() => setRegToast(null)} className="text-white/40 hover:text-white transition-colors shrink-0" title="Fermer" aria-label="Fermer">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const StatusBar = () => {
    const { canvas, selectedElementIds, elements } = useLabelStore();
    const selCount = selectedElementIds.length;
    let contextMsg = "Sélectionnez un élément (Maj+Clic = Multisélection)";
    if (selCount === 1) {
        const el = elements.find(e => e.id === selectedElementIds[0]);
        if (el) contextMsg = `Élément sélectionné : ${el.name || el.type}`;
    } else if (selCount > 1) {
        contextMsg = `${selCount} éléments sélectionnés (Déplacement groupé actif)`;
    }

    return (
        <div className="h-7 bg-forge-bg border-t border-white/10 flex items-center justify-between px-4 text-[10px] text-white/40 font-mono shrink-0 z-20">
            <div className="flex items-center gap-4">
                <span className="text-white">{contextMsg}</span>
                <span className="opacity-50 border-l border-white/10 pl-4">Main maintenue (Espace) = Déplacement de la vue</span>
            </div>
            <div className="flex items-center gap-4 text-white">
                <span>Format : {canvas.width_mm} x {canvas.height_mm} mm</span>
                <span>Zoom : {Math.round(canvas.scale * 100)}%</span>
            </div>
        </div>
    );
};

// Languette d'onglets verticale, affichée à gauche du dock de droite quand le
// Studio I.A est ouvert. Permet de basculer Studio I.A ⇄ Inspecteur sans fermer
// le Studio. L'onglet Studio I.A reprend l'identité visuelle (accent + logo P)
// du bouton central, pour qu'on voie immédiatement que le Studio est actif.
const RightDockTabs = ({ view, onSelectStudio, onSelectFds, onSelectInspector, onSelectHelp, onClose }) => {
    const labelStyle = { writingMode: 'vertical-rl', transform: 'rotate(180deg)' };
    // La croix du bas appelle closePanel() : elle referme la colonne de droite
    // ENTIÈRE, donc la vue affichée, quelle qu'elle soit. Elle annonçait pourtant
    // « Fermer le Studio I.A » en permanence : sur le FDS Studio ou sur
    // l'inspecteur, l'infobulle et le lecteur d'écran promettaient de fermer un
    // panneau que l'utilisateur n'avait pas sous les yeux. Le libellé suit
    // maintenant la vue courante.
    // La vue « help » est arrivée avec le Guide, après ce libellé : sans elle, la
    // croix annonçait « Fermer le Studio I.A » alors qu'elle refermait le manuel.
    const LIBELLES_FERMETURE = {
        fds: 'Fermer le FDS Studio',
        inspector: "Fermer la languette et garder l'inspecteur",
        help: 'Fermer le Guide',
        studio: 'Fermer le Studio I.A',
    };
    const closeLabel = LIBELLES_FERMETURE[view] || 'Fermer la colonne de droite';
    return (
        <div className="flex flex-col items-stretch shrink-0 w-11 bg-black/30 border-l border-white/5 py-2 gap-2 relative z-[45] shadow-2xl">
            {/* Onglet Studio I.A — même look que le bouton central (accent + P) */}
            <button
                onClick={onSelectStudio}
                title="Studio I.A"
                className={`mx-1 rounded-lg flex flex-col items-center justify-center gap-2 py-3 border transition-all ${
                    view === 'studio'
                        ? 'bg-forge-accent text-white border-white/10 shadow-[0_0_14px_rgba(var(--forge-accent),0.5)]'
                        : 'bg-forge-accent/15 text-white/75 border-forge-accent/30 hover:bg-forge-accent/25 hover:text-white'
                }`}
            >
                <img src={polyprintP} alt="" className="h-4 w-auto" draggable="false" />
                <span style={labelStyle} className="text-[9px] font-mono font-bold uppercase tracking-[0.2em]">Studio IA</span>
            </button>

            {/* Onglet FDS Studio — couleur emerald (distincte du Studio I.A) */}
            <button
                onClick={onSelectFds}
                title="FDS Studio" aria-label="FDS Studio"
                className={`mx-1 rounded-lg flex flex-col items-center justify-center gap-2 py-3 border transition-all ${
                    view === 'fds'
                        ? 'bg-emerald-500 text-white border-white/10 shadow-[0_0_14px_rgba(16,185,129,0.5)]'
                        : 'bg-emerald-500/15 text-white/75 border-emerald-500/30 hover:bg-emerald-500/25 hover:text-white'
                }`}
            >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" />
                </svg>
                <span style={labelStyle} className="text-[9px] font-mono font-bold uppercase tracking-[0.2em]">FDS Studio</span>
            </button>

            {/* Onglet Inspecteur */}
            <button
                onClick={onSelectInspector}
                title="Inspecteur" aria-label="Inspecteur"
                className={`mx-1 rounded-lg flex flex-col items-center justify-center gap-2 py-3 border transition-all ${
                    view === 'inspector'
                        ? 'bg-white/15 text-white border-white/20'
                        : 'bg-black/20 text-white/50 border-white/5 hover:bg-white/10 hover:text-white/80'
                }`}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                    <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                    <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
                </svg>
                <span style={labelStyle} className="text-[9px] font-mono font-bold uppercase tracking-[0.2em]">Inspecteur</span>
            </button>

            {/* Onglet Guide — bleu ciel, la couleur du manuel (cf. GuidePanel) */}
            <button
                onClick={onSelectHelp}
                title="Guide d'utilisation" aria-label="Guide d'utilisation"
                className={`mx-1 rounded-lg flex flex-col items-center justify-center gap-2 py-3 border transition-all ${
                    view === 'help'
                        ? 'text-white border-white/10'
                        : 'text-white/75 hover:text-white'
                }`}
                style={view === 'help'
                    ? { background: 'rgba(var(--forge-sky),1)', boxShadow: '0 0 14px rgba(var(--forge-sky),0.5)' }
                    : { background: 'rgba(var(--forge-sky),0.15)', borderColor: 'rgba(var(--forge-sky),0.30)' }}
            >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9.5" />
                    <path d="M9.6 9.2a2.5 2.5 0 1 1 3.4 2.3c-.72.3-1 .92-1 1.63v.37" />
                    <line x1="12" y1="17" x2="12" y2="17.01" />
                </svg>
                <span style={labelStyle} className="text-[9px] font-mono font-bold uppercase tracking-[0.2em]">Guide</span>
            </button>

            {/* Refermer la colonne de droite → il ne reste que l'inspecteur */}
            <button
                onClick={onClose}
                title={closeLabel} aria-label={closeLabel}
                className="mx-1 mt-auto rounded-lg py-2.5 flex items-center justify-center text-white/40 hover:text-forge-rose hover:bg-forge-rose/10 border border-transparent hover:border-forge-rose/30 transition-all"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
        </div>
    );
};

const ShapeLibraryRightPanel = ({ onAddSvgShape, onClose }) => {
    const [panelWidth, setPanelWidth] = React.useState(380);
    const isResizing = React.useRef(false);

    React.useEffect(() => {
        const move = (e) => { if (isResizing.current) setPanelWidth(Math.min(Math.max(300, document.body.clientWidth - e.clientX), 700)); };
        const up = () => { if (isResizing.current) { isResizing.current = false; document.body.style.cursor = 'default'; } };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    }, []);

    return (
        <div className="bg-black/40 backdrop-blur-xl border-l border-white/5 flex shrink-0 relative shadow-2xl transition-all overflow-hidden" style={{ width: panelWidth }}>
            <div className="absolute left-0 top-0 bottom-0 w-[6px] cursor-col-resize z-50 hover:bg-white/50 transition-colors" style={{ transform: 'translateX(-50%)' }}
                onMouseDown={(e) => { e.preventDefault(); isResizing.current = true; document.body.style.cursor = 'col-resize'; }} />
            <ShapeLibraryPanel onAddSvgShape={onAddSvgShape} onClose={onClose} />
        </div>
    );
};

export default function EditorView() {
    const addElement = useLabelStore(state => state.addElement);
    const setAiImage = useLabelStore(state => state.setAiImage);
    const canvas = useLabelStore(state => state.canvas);
    const aiModel = useLabelStore(state => state.aiModel);
    const uiTheme = useLabelStore(state => state.uiTheme);
    const aiStudioOpen = useAIStudioStore(state => state.isOpen);
    const aiStudioView = useAIStudioStore(state => state.view);

    // Multi-tab sync
    useTabSync();

    // Resizing States
    const [leftWidth, setLeftWidth] = useState(288); // w-72 = 288px
    const isResizingLeft = React.useRef(false);

    React.useEffect(() => {
        const handleMouseMove = (e) => {
            if (isResizingLeft.current) {
                setLeftWidth(Math.min(Math.max(200, e.clientX), 600));
            }
        };
        const handleMouseUp = () => {
            if (isResizingLeft.current) {
                isResizingLeft.current = false;
                document.body.style.cursor = 'default';
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    // --- AI Generation State ---
    const [prompt, setPrompt] = useState("");
    const [jobId, setJobId] = useState(null);
    const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
    const [isInspirationModalOpen, setIsInspirationModalOpen] = useState(false);
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [customWorkflow, setCustomWorkflow] = useState(null);
    const [randomizeSeed, setRandomizeSeed] = useState(true);
    const [isGeneratingLocally, setIsGeneratingLocally] = useState(false);
    const [isPrefsOpen, setIsPrefsOpen] = useState(false);
    const [isPreflightOpen, setIsPreflightOpen] = useState(false);
    const [isShapeLibOpen, setIsShapeLibOpen] = useState(false);
    const [showEasterEgg, setShowEasterEgg] = useState(false);
    const [showPresentation, setShowPresentation] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [showFilesColumn, setShowFilesColumn] = useState(false);
    const easterClicksRef = React.useRef(0);
    const easterTimerRef = React.useRef(null);

    React.useEffect(() => {
        const handleOpenPrefs = () => setIsPrefsOpen(true);
        window.addEventListener('open-preferences', handleOpenPrefs);
        return () => window.removeEventListener('open-preferences', handleOpenPrefs);
    }, []);

    // F5 → Mode Présentation
    React.useEffect(() => {
        const handlePresentation = () => setShowPresentation(true);
        window.addEventListener('labelforge:presentation', handlePresentation);
        return () => window.removeEventListener('labelforge:presentation', handlePresentation);
    }, []);

    // --- MISE À JOUR DU THEME UI GLOBAL ---
    // Le slider de luminosite a ete retire au profit de vrais themes clairs
    // dedies (forge-*-light). Un seul useEffect suffit maintenant.
    React.useEffect(() => {
        if (uiTheme) {
            document.documentElement.setAttribute('data-theme', uiTheme);
            // Miroir synchrone pour l'application avant premier rendu (index.html)
            try { localStorage.setItem('labelforge-ui-theme', uiTheme); } catch { /* stockage plein/privé */ }
        }
    }, [uiTheme]);

    const { progress, previewImages, status, error, urls, statusLog, duration } = useJobSocket(jobId);

    const [lastCompletedJob, setLastCompletedJob] = useState(null);
    const processedJobsRef = React.useRef(new Set());

    // Mettre à jour l'image du Canvas quand ComfyUI renvoie une preview OU un URL final
    React.useEffect(() => {
        // En cours de génération : on peut garder un aperçu visuel (on prend la première image par défaut)
        if (status === 'running' && previewImages && previewImages.length > 0) {
            setAiImage({ url: previewImages[0] });
        }

        // Dès que la tâche est complétée, on force la conversion en calque !
        if (status === 'completed' && jobId && !processedJobsRef.current.has(jobId)) {
            processedJobsRef.current.add(jobId);
            setAiImage({ url: null }); // Retire le fond temporaire

            // N'auto-injecte QUE si la modale avancée est fermée
            if (!isAdvancedModalOpen) {
                // Calcul sécurisé des dimensions par défaut
                const cw = Number(canvas.width_mm) || 130;
                const ch = Number(canvas.height_mm) || 80;

                // Au pire des cas, si la route API échoue, on utilise le base64 récupéré du websocket
                const fallbackImg = previewImages.length > 0 ? previewImages[0] : null;
                const finalImage = (urls && urls.preview_url) ? `${API_URL}${urls.preview_url}` : fallbackImg;

                if (finalImage) {
                    const img = new Image();
                    img.onload = () => {
                        // Injection à la taille exacte basée sur une résolution d'impression standard (300 DPI)
                        const w = img.width * 25.4 / 300;
                        const h = img.height * 25.4 / 300;

                        addElement({
                            type: 'image',
                            url: finalImage,
                            isAI: true,
                            width: w,
                            height: h,
                            x: cw / 2 - w / 2,
                            y: ch / 2 - h / 2,
                            rotation: 0
                        });
                        historiser('Ajout : image IA');
                    };
                    img.src = finalImage;
                }
            }
        }
    }, [status, jobId, urls, previewImages, addElement, canvas.width_mm, canvas.height_mm, setAiImage, isAdvancedModalOpen]);

    const handleInjectFromModal = (selectedImageIndex = 0) => {
        const cw = Number(canvas.width_mm) || 130;
        const ch = Number(canvas.height_mm) || 80;
        let finalImage = null;

        if (urls && urls.preview_url && previewImages.length <= 1) {
            finalImage = `${API_URL}${urls.preview_url}`;
        } else if (previewImages && previewImages.length > selectedImageIndex) {
            finalImage = previewImages[selectedImageIndex];
        }

        if (finalImage) {
            const img = new Image();
            img.onload = () => {
                // Injection à la taille exacte basée sur une résolution d'impression standard (300 DPI)
                const w = img.width * 25.4 / 300;
                const h = img.height * 25.4 / 300;

                addElement({
                    type: 'image',
                    url: finalImage,
                    isAI: true,
                    width: w,
                    height: h,
                    x: cw / 2 - w / 2,
                    y: ch / 2 - h / 2,
                    rotation: 0
                });
                historiser('Ajout : image IA');
            };
            img.src = finalImage;

            setIsAdvancedModalOpen(false); // Ferme la modale pour voir l'étiquette
        }
    };

    const handleGenerateAI = async (overrideWf = null) => {
        if (isGeneratingLocally) return;
        setIsGeneratingLocally(true);
        try {
            const payload = {
                prompt_keywords: prompt || "AI Generated",
                flavor_name: "AI Prompt",
                brand_name: "AI",
                include_ean13: false,
                randomize_seed: randomizeSeed,
                unet_model: aiModel
            };

            const wfToUse = overrideWf || customWorkflow;
            if (wfToUse) {
                payload.custom_workflow = wfToUse;
            }

            const job = await createJob(payload);
            setJobId(job.id);
        } catch (err) {
            console.error(err);
        } finally {
            setIsGeneratingLocally(false);
        }
    };

    // L'ajout d'un élément n'entrait PAS dans l'historique : on pouvait annuler
    // son déplacement, jamais sa création. Même motif que la duplication et le
    // collage — le `setTimeout(…, 0)` laisse `addElement` finir son `set()` avant
    // la prise de l'instantané. Constaté le 16/09/2026 en préparant une formation.
    const historiser = (nom) => setTimeout(() => useLabelStore.getState()._pushState(nom), 0);

    const addText = () => {
        addElement({
            type: 'text',
            text: 'Nouveau Texte',
            font: 'Oswald',
            size: 10,
            // Couleur de contraste auto par rapport au fond du document :
            // fond clair → texte noir, fond foncé → texte blanc. Avant on
            // hardcodait #FFFFFF qui rendait le calque invisible sur fond
            // blanc (cas le plus fréquent).
            color: pickContrastColor(canvas.color_bg),
            x: 20,
            y: 20,
            width: 100,
            height: 20,
            textAlign: 'left'
        });
        historiser('Ajout : texte');
    };

    const addBarcode = () => {
        addElement({
            type: 'barcode',
            symbology: 'ean13',
            text: '123456789012',
            showText: true,
            font: 'Oswald',
            size: 8,
            color: '#000000',
            bgColor: '#FFFFFF',
            bcScale: 3,
            bcHeight: 15,
            bcMargin: 0,
            bcInkSpread: 0,
            x: 50,
            y: 50,
            width: 60,
            height: 30,
            textAlign: 'center'
        });
        historiser('Ajout : code-barres');
    };

    const addShape = (shapeType) => {
        let borderRadius = 0;
        if (shapeType === 'rounded-rect') borderRadius = 10;
        else if (shapeType === 'circle') borderRadius = 50; // We'll manage circle as 50% border radius in styles, but stored as 50 for consistency

        addElement({
            type: 'shape',
            shapeType: shapeType, // 'rect', 'circle', 'rounded-rect'
            x: canvas.width_mm / 2 - 20,
            y: canvas.height_mm / 2 - 20,
            width: 40,
            height: 40,
            color: '#888888', // Default fill color
            borderRadius: borderRadius,
            rotation: 0
        });
        historiser('Ajout : forme');
    };

    const addSvgShape = (shape) => {
        const isMultiColor = shape.multiColor && shape.svgContent;
        addElement({
            type: 'shape',
            shapeType: 'svg',
            svgPath: isMultiColor ? null : shape.path,
            svgContent: isMultiColor ? shape.svgContent : null,
            svgViewBox: shape.viewBox,
            svgFill: shape.fill !== false,
            x: canvas.width_mm / 2 - 15,
            y: canvas.height_mm / 2 - 15,
            width: 30,
            height: 30,
            color: isMultiColor ? null : pickContrastColor(canvas.color_bg),
            rotation: 0,
            name: shape.name,
        });
        historiser(`Ajout : ${shape.name || 'forme'}`);
    };

    const imageInputRef = React.useRef(null);
    const handleImportImage = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const ratio = img.width / img.height;
                const w = 50;
                addElement({ type: 'image', url: event.target.result, isAI: false, width: w, height: w / ratio, x: canvas.width_mm / 2 - w / 2, y: canvas.height_mm / 2 - (w / ratio) / 2, rotation: 0 });
                historiser('Ajout : image');
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    return (
        <div className="flex w-full h-full overflow-hidden">

            {/* LEFT PANEL : Tools & AI */}
            <aside
                className="bg-black/40 backdrop-blur-xl border-r border-white/5 flex flex-col z-[50] shrink-0 relative shadow-2xl transition-all"
                style={{ width: `${leftWidth}px` }}
            >
                {/* Resize Handle Gauche */}
                <div
                    className="absolute -right-[3px] top-0 bottom-0 w-[6px] cursor-col-resize z-50 hover:bg-white/50 transition-colors"
                    onMouseDown={(e) => { e.preventDefault(); isResizingLeft.current = true; document.body.style.cursor = 'col-resize'; }}
                />

                <div className="p-3 border-b border-white/5 bg-black/20 flex items-center gap-3">
                    {/* Logo blanc d'origine → en thème clair on inverse les
                        couleurs via CSS (filter: invert) pour qu'il devienne
                        noir et reste lisible. Cf. globals.css `.logo-adaptive`. */}
                    <img src={logo} alt="PolyPrint Studio" className="h-6 object-contain cursor-pointer logo-adaptive" onClick={() => {
                        easterClicksRef.current++;
                        if (easterTimerRef.current) clearTimeout(easterTimerRef.current);
                        easterTimerRef.current = setTimeout(() => { easterClicksRef.current = 0; }, 2000);
                        if (easterClicksRef.current >= 10) { easterClicksRef.current = 0; setShowEasterEgg(true); }
                    }} />
                    <h1 className="font-mono text-[13px] font-bold text-white truncate tracking-wider drop-shadow-md">LabelForge Studio</h1>
                </div>

                <div className="flex-1 overflow-y-auto w-full custom-scrollbar">
                    <div className="p-4 flex flex-col h-full">
                        <ZoneProtegee nom="Calques">
                            <LayersPanel
                                onPresent={() => window.dispatchEvent(new CustomEvent('labelforge:presentation'))}
                                onShare={() => setIsShareOpen(true)}
                                onToggleFiles={() => setShowFilesColumn(v => !v)}
                                filesOpen={showFilesColumn}
                            />
                        </ZoneProtegee>
                        <input type="file" accept="image/png, image/jpeg, image/svg+xml" ref={imageInputRef} onChange={handleImportImage} className="hidden" />

                        {error && (
                            <div className="mt-4 p-3 bg-red-900/30 border border-red-800 rounded text-xs text-red-300">
                                {error}
                            </div>
                        )}

                    </div>
                </div>

                {/* Barre d'état — épinglée en bas de la colonne, toujours
                    visible (hors du conteneur qui défile). Version + flèche
                    de mise à jour. */}
                <div className="shrink-0 border-t border-white/5 bg-black/20 px-3 py-1.5 flex items-center justify-between">
                    <VersionBadge />
                </div>
            </aside>

            {/* COLONNE FICHIERS OUVERTS — entre la sidebar gauche et le canvas.
                Affichée à la demande (bouton « Fichiers » du LayersPanel). */}
            {showFilesColumn && <FilesColumn onClose={() => setShowFilesColumn(false)} />}

            {/* CENTER : Canvas */}
            <main className="flex-1 bg-transparent relative overflow-hidden flex flex-col z-0">
                {/* Top Navbar */}
                <TopActionBar
                    onOpenAI={() => setIsAdvancedModalOpen(true)}
                    onOpenInspiration={() => useAIStudioStore.getState().pressStudioButton()}
                    onOpenPreflight={() => setIsPreflightOpen(true)}
                    onOpenNew={() => setIsNewModalOpen(true)}
                    onOpenProfile={() => setIsProfileOpen(true)}
                    onOpenShare={() => setIsShareOpen(true)}
                    onOpenExport={() => setShowExportDialog(true)}
                    onAddText={addText}
                    onAddBarcode={addBarcode}
                    onAddImage={() => imageInputRef.current?.click()}
                    onAddShape={addShape}
                    onOpenShapeLibrary={() => { setIsShapeLibOpen(true); useAIStudioStore.getState().closePanel(); }}
                    aiStatus={status}
                    aiProgress={progress}
                    isGeneratingLocally={isGeneratingLocally}
                />

                {/* Réglages de l'outil actif — n'occupe de la hauteur que
                    lorsqu'un outil en expose. Les deux barres s'excluent : un
                    seul outil est actif à la fois, il n'y en a jamais deux
                    empilées. */}
                <EraserOptionsBar />
                <SelectionOptionsBar />

                {/* Document Tabs */}
                <TabBar />

                {/* Scrollable Canvas Area / Infinity Canvas */}
                < div className="flex-1 overflow-hidden flex items-center justify-center relative" id="canvas-viewport" >
                    <ZoneProtegee nom="Plan de travail" className="w-full h-full">
                        <LabelCanvas />
                        <FloatingNavigator />
                    </ZoneProtegee>
                </div >

                {/* Palette d'outils flottante — rendue en portal, elle n'est ici
                    que pour recevoir les mêmes actions que le menu « Ajout ». */}
                <ToolPalette
                    onAddText={addText}
                    onAddImage={() => imageInputRef.current?.click()}
                    onAddBarcode={addBarcode}
                    onAddShape={addShape}
                    onOpenShapeLibrary={() => { setIsShapeLibOpen(true); useAIStudioStore.getState().closePanel(); }}
                />

                {/* Bottom Status Bar */}
                <StatusBar />
            </main >

            {/* RIGHT PANEL : Inspector / AI Studio / Shape Library.
                Quand le Studio I.A est ouvert, une languette d'onglets (RightDockTabs)
                permet de basculer Studio I.A ⇄ Inspecteur SANS fermer le Studio
                (qui reste "actif"). L'onglet Studio I.A reprend le style accent du
                bouton central pour qu'on voie tout de suite que le Studio est ouvert.
                AIStudioPanel est lazy → wrap Suspense seulement quand il est demandé.
                Chaque vue a sa propre zone protégée (`key`) : si le Studio tombe,
                les onglets restent là, et passer à l'Inspecteur repart d'une zone neuve. */}
            {aiStudioOpen ? (
                <>
                    <RightDockTabs
                        view={aiStudioView}
                        onSelectStudio={() => useAIStudioStore.getState().setView('studio')}
                        onSelectFds={() => useAIStudioStore.getState().setView('fds')}
                        onSelectInspector={() => useAIStudioStore.getState().setView('inspector')}
                        onSelectHelp={() => useAIStudioStore.getState().setView('help')}
                        onClose={() => useAIStudioStore.getState().closePanel()}
                    />
                    <ZoneProtegee
                        key={aiStudioView}
                        nom={{ studio: 'Studio IA', fds: 'Fiches réglementaires', help: 'Guide' }[aiStudioView] || 'Inspecteur'}
                        className={REPLI_DROITE}
                    >
                        {aiStudioView === 'studio' ? (
                            <Suspense fallback={<LazyOverlayFallback />}>
                                <AIStudioPanel />
                            </Suspense>
                        ) : aiStudioView === 'fds' ? (
                            <FdsStudioPanel />
                        ) : aiStudioView === 'help' ? (
                            <Suspense fallback={<LazyOverlayFallback />}>
                                <GuidePanel />
                            </Suspense>
                        ) : <Inspector />}
                    </ZoneProtegee>
                </>
            ) : (
                <ZoneProtegee
                    key={isShapeLibOpen ? 'formes' : 'inspecteur'}
                    nom={isShapeLibOpen ? 'Bibliothèque de formes' : 'Inspecteur'}
                    className={REPLI_DROITE}
                >
                    {isShapeLibOpen ? (
                        <ShapeLibraryRightPanel onAddSvgShape={addSvgShape} onClose={() => setIsShapeLibOpen(false)} />
                    ) : <Inspector />}
                </ZoneProtegee>
            )}

            {/* AdvancedComfyModal : lazy + conditionné par isAdvancedModalOpen pour
                éviter de fetcher son chunk tant que l'utilisateur ne l'ouvre pas. */}
            {isAdvancedModalOpen && (
                <ZoneProtegee nom="Studio avancé" className={REPLI_SUPERPOSE} onFermer={() => setIsAdvancedModalOpen(false)}>
                <Suspense fallback={<LazyOverlayFallback />}>
                    <AdvancedComfyModal
                        isOpen={isAdvancedModalOpen}
                        onClose={() => setIsAdvancedModalOpen(false)}
                        initialWorkflow={customWorkflow}
                        onSave={setCustomWorkflow}
                        onGenerate={(wf) => {
                            setCustomWorkflow(wf);
                            handleGenerateAI(wf);
                        }}
                        onInject={handleInjectFromModal}
                        status={status}
                        progress={progress}
                        statusLog={statusLog}
                        globalError={error}
                        previewImages={previewImages}
                        randomizeSeed={randomizeSeed}
                        setRandomizeSeed={setRandomizeSeed}
                        duration={duration}
                    />
                </Suspense>
                </ZoneProtegee>
            )}

            <InspirationStudioModal isOpen={isInspirationModalOpen} onClose={() => setIsInspirationModalOpen(false)} />
            <NewDocumentModal isOpen={isNewModalOpen} onClose={() => setIsNewModalOpen(false)} />
            <PreferencesModal isOpen={isPrefsOpen} onClose={() => setIsPrefsOpen(false)} />
            <PdfPreflightModal isOpen={isPreflightOpen} onClose={() => setIsPreflightOpen(false)} />
            {showEasterEgg && (
                <ZoneProtegee nom="VapeInvaders" className={REPLI_SUPERPOSE} onFermer={() => setShowEasterEgg(false)}>
                    <Suspense fallback={<LazyOverlayFallback />}>
                        <VapeInvaders onClose={() => setShowEasterEgg(false)} apiUrl={API_URL} />
                    </Suspense>
                </ZoneProtegee>
            )}
            {showPresentation && (
                <ZoneProtegee nom="Présentation" className={REPLI_SUPERPOSE} onFermer={() => setShowPresentation(false)}>
                    <Suspense fallback={<LazyOverlayFallback />}>
                        <PresentationMode onExit={() => setShowPresentation(false)} />
                    </Suspense>
                </ZoneProtegee>
            )}
            <UserProfileModal isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} />
            <ShareModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)} />
            <ExportDialog isOpen={showExportDialog} onClose={() => setShowExportDialog(false)} />
        </div >
    );
}
